
import React, { useState, useMemo, useEffect } from 'react';
import { getDefaultCutoffFromStart, toIsoDate, toMonthDayLabel, getCancelDeadlineDate, getEventStartDate } from '../utils/dateUtils';
import {
  CheckCircle,
  Search,
  User,
  Upload,
  Plus,
  Trash2,
  Settings,
  List,
  Calendar,
  FileSpreadsheet,
  ChevronDown,
  Database,
  HelpCircle,
  ShieldAlert,
  UserCheck,
  Shield,
  Pencil,
  X,
  Save,
  Stethoscope,
  AlertTriangle,
  RotateCw,
  Phone,
  Lock,
  Unlock,
  Users,
  ClipboardList,
  Package,
  Sparkles,
  ArrowUpDown
} from 'lucide-react';
import { Registrant, HistoricalRecord, EventOption, WatchlistItem, Gender, District, EventType, TripParticipant } from '../types';
import { checkEligibility, parseHistoricalCSV } from '../services/verificationService';
import {
  getAllowedAdmins,
  getAllowedInventoryUsers,
  updateAllowedAdmins,
  updateAllowedInventoryUsers,
  getWatchlist,
  updateWatchlist,
  clearAllHistoricalData
} from '../services/firebaseService';
import { sendConfirmationEmail } from '../services/emailService';
import { LEADERSHIP_ROLES, TASK_ROLES, OFFICIAL_SUPPORT_ROLES, ROLE_CATEGORIES } from '../constants';
import { ToastType } from './Toast';
import ScheduleEditor from './ScheduleEditor';
import OutlookRecords from './OutlookRecords';
import InventoryManager from './InventoryManager';
import { db } from '../firebase';
import { serverTimestamp, writeBatch, doc } from 'firebase/firestore';
import { sanitizeName, sanitizePhone, sanitizeChurch, sanitizeText } from '../utils/sanitize';
import { formatOfficialSupportAvailability, isOfficialSupportEvent } from '../utils/officialSupport';
import { buildPublicRosterPayload } from '../utils/publicRoster';
import {
  buildFirstTimeParticipantIndex,
  isFirstTimeParticipantWithIndex,
  FIRST_TIME_PARTICIPANT_TOOLTIP
} from '../utils/firstTimeParticipant';

const normalizePhone = (p: string) => p.replace(/\D/g, '');

const TASK_FILTER_ALL = 'all';
const ROLE_FILTER_ALL = 'all';
const ROLE_FILTER_ORDER = Object.values(ROLE_CATEGORIES).flat();
const ROLE_FILTER_ALIASES: Record<string, string> = {
  護理: '護理師',
  物理治療: '物理治療師',
};

const compareEventsChronologically = (a: EventOption, b: EventOption): number => {
  const startA = getEventStartDate(a)?.getTime() ?? null;
  const startB = getEventStartDate(b)?.getTime() ?? null;

  if (startA === null || startB === null) {
    if (startA !== startB) return startA === null ? 1 : -1;
  } else if (startA !== startB) {
    return startA - startB;
  }

  return a.dateRange.localeCompare(b.dateRange, 'zh-Hant')
    || a.location.localeCompare(b.location, 'zh-Hant')
    || a.id.localeCompare(b.id);
};

const formatTaskDisplay = (task: string, subTask?: string) => {
  if (!task || task === '尚未分配') return '尚未分配';
  if ((task === '跟診' || task === '流動') && subTask) return `${task}（${subTask}）`;
  if (task === '其他' && subTask) return subTask;
  return task;
};

const getTaskFilterKey = (task: string, subTask?: string) => {
  if (!task || task === '尚未分配') return '尚未分配';
  if (task === '其他' && subTask) return subTask;
  return task;
};

const CHINESE_NUMERALS = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十'];

const sectionNumber = (index: number) => CHINESE_NUMERALS[index] || String(index + 1);

type WorkAssignmentStation = {
  title: string;
  aliases: readonly string[];
  primaryLabel?: string;
};

const WORK_ASSIGNMENT_STATIONS: readonly WorkAssignmentStation[] = [
  { title: '掛號', aliases: ['掛號', '櫃台'], primaryLabel: '問診' },
  { title: '西醫', aliases: ['西醫'] },
  { title: '中醫', aliases: ['中醫'] },
  { title: '物理治療', aliases: ['物理治療', '物理治療師'] },
  { title: '牙科', aliases: ['牙科', '牙醫'] },
  { title: '眼科', aliases: ['眼科'] },
  { title: '器消', aliases: ['器消', '器械載運', '供應室/器械消毒'] },
  { title: '醫檢', aliases: ['醫檢', '醫檢/心電圖'] },
  { title: '藥局', aliases: ['藥局'] },
  { title: '衛教', aliases: ['衛教', '衛教/衛生護理教育'] },
  { title: '義剪', aliases: ['義剪'] },
  { title: '雅歌音樂', aliases: ['雅歌音樂', '雅歌事奉樂團'] },
  { title: '資訊.紀錄', aliases: ['資訊.紀錄', '資訊紀錄'] },
  { title: '庶務.攝影', aliases: ['庶務.攝影', '庶務攝影', '媒體宣道/攝影', '總務/庶務'] },
  { title: '心靈關懷', aliases: ['心靈關懷', '心靈關懷/宣道'] },
  { title: '小小醫師營', aliases: ['小小醫師營'] },
] as const;

const WORK_ASSIGNMENT_STATION_TARGETS = WORK_ASSIGNMENT_STATIONS.map(station => station.title);
const WORK_ASSIGNMENT_EXPORT_ORDER = WORK_ASSIGNMENT_STATION_TARGETS;

const normalizeWorkStation = (value?: string) => {
  const normalized = (value || '').trim();
  if (!normalized) return '';

  const station = WORK_ASSIGNMENT_STATIONS.find(item =>
    item.title === normalized || (item.aliases as readonly string[]).includes(normalized)
  );

  return station?.title || normalized;
};

const getWorkStationConfig = (title: string) =>
  WORK_ASSIGNMENT_STATIONS.find(station => station.title === title);

const taskSupportsSubDetail = (task: string) => task === '跟診' || task === '流動';
const isDentalAssistantTask = (task: string) => ['牙科跟診/牙助', '牙助', '牙醫助理'].includes(task);
const taskSupportsAssistTarget = (task: string) => task === '跟診' || isDentalAssistantTask(task);

const formatRocDate = (isoDate?: string) => {
  const date = isoDate ? new Date(`${isoDate}T00:00:00`) : new Date();
  const validDate = isNaN(date.getTime()) ? new Date() : date;
  const rocYear = validDate.getFullYear() - 1911;
  const month = String(validDate.getMonth() + 1).padStart(2, '0');
  const day = String(validDate.getDate()).padStart(2, '0');
  return `${rocYear}.${month}.${day}`;
};

// 將報名時間戳記格式化為「YYYY/MM/DD HH:mm」，無效值回傳空字串
const formatSubmissionTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
};

const TABLE_HEADER_CELL = 'px-4 py-4 align-middle text-sm font-bold leading-5 whitespace-nowrap [word-break:keep-all]';
const TABLE_BODY_CELL = 'px-4 py-4 align-middle text-sm leading-6';
const ASSIGNMENT_SELECT_CLASS = 'appearance-none w-full min-h-11 px-3 py-2 rounded-lg text-sm font-bold leading-5 border transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 text-center cursor-pointer whitespace-nowrap';
const EMPTY_FIELD_LABEL = '未填';

// --- 每梯報名名單排序 ---
type RegistrantSortMode = 'default' | 'submitted-asc' | 'submitted-desc' | 'name' | 'church' | 'first-time';

const REGISTRANT_SORT_OPTIONS: { value: RegistrantSortMode; label: string }[] = [
  { value: 'default', label: '預設（待審核優先）' },
  { value: 'submitted-asc', label: '報名時間（先→後）' },
  { value: 'submitted-desc', label: '報名時間（後→先）' },
  { value: 'name', label: '姓名' },
  { value: 'church', label: '教會' },
  { value: 'first-time', label: '第一次參與優先' },
];

const REGISTRANT_SORT_STORAGE_KEY = 'tjcmed.dashboard.registrantSort';

const isRegistrantSortMode = (value: unknown): value is RegistrantSortMode =>
  REGISTRANT_SORT_OPTIONS.some(option => option.value === value);

const readStoredRegistrantSort = (): RegistrantSortMode => {
  try {
    const stored = window.localStorage.getItem(REGISTRANT_SORT_STORAGE_KEY);
    return isRegistrantSortMode(stored) ? stored : 'default';
  } catch {
    return 'default';
  }
};

// 報名時間轉毫秒；缺漏或無法解析回傳 null（排序時一律放最後）
const getSubmissionTimestamp = (iso?: string): number | null => {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? null : time;
};

const getDisplayRoles = (registrant: Registrant): string[] => {
  const roles = registrant.roles && registrant.roles.length > 0 ? registrant.roles : [registrant.role];
  return roles.map(role => role.trim()).filter(Boolean);
};

const formatRosterRoles = (registrant: Registrant): string =>
  getDisplayRoles(registrant).map(role => {
    if (role === '學生' && registrant.studentSpecialization) {
      const specialization = registrant.studentSpecialization === '其他（請說明）' && registrant.otherRoleDescription
        ? `其他：${registrant.otherRoleDescription}`
        : registrant.studentSpecialization;
      return `${role}（${specialization}）`;
    }

    if (role === '西醫' && registrant.westernMedicineSpecialty) {
      return `${role}（${registrant.westernMedicineSpecialty}）`;
    }

    if (role === '治療師' && registrant.therapistSpecialty) {
      return `${role}（${registrant.therapistSpecialty}）`;
    }

    return role;
  }).join('、');

const getRoleBaseName = (role: string): string => role.split(/[~(（]/)[0].trim();

const getRoleFilterKey = (role: string): string => {
  const baseName = getRoleBaseName(role);
  return ROLE_FILTER_ALIASES[baseName] || baseName;
};

const getRegistrantRoleKeys = (registrant: Registrant): string[] =>
  Array.from(new Set(getDisplayRoles(registrant).map(getRoleFilterKey).filter(Boolean)));

const sortRoleFilterOptions = (a: string, b: string) => {
  const aIndex = ROLE_FILTER_ORDER.indexOf(a);
  const bIndex = ROLE_FILTER_ORDER.indexOf(b);
  const normalizedAIndex = aIndex === -1 ? ROLE_FILTER_ORDER.length : aIndex;
  const normalizedBIndex = bIndex === -1 ? ROLE_FILTER_ORDER.length : bIndex;
  return normalizedAIndex - normalizedBIndex || a.localeCompare(b, 'zh-Hant');
};

const requiresOfficialSupportDisplay = (registrant: Registrant): boolean =>
  getDisplayRoles(registrant).some(role => OFFICIAL_SUPPORT_ROLES.includes(getRoleFilterKey(role)));

const emptyMemberEditForm = {
  id: '',
  name: '',
  email: '',
  phone: '',
  church: '',
  gender: '',
  role: '',
  otherRoleDescription: '',
  studentSpecialization: '',
  westernMedicineSpecialty: '',
  therapistSpecialty: '',
  message: '',
};

interface DashboardProps {
  registrations: Registrant[];
  historicalData: HistoricalRecord[];
  tripParticipants?: TripParticipant[];
  watchlist: WatchlistItem[];
  events: EventOption[];
  isEventDataLoading?: boolean;
  systemYear: number;
  onUpdateSystemYear: (year: number) => void;
  onUpdateHistoricalData: (data: HistoricalRecord[]) => void;
  onUpdateWatchlist: (data: WatchlistItem[]) => void;
  onUpdateEvents: (events: EventOption[]) => void;
  onEditEvent: (event: EventOption) => void;
  onUpdateEventAndRegistrants: (
    event: EventOption,
    registrants: Registrant[],
    successTitle: string,
    successMessage: string
  ) => Promise<void>;
  onCreateRegistrant: (registrant: Omit<Registrant, 'id'>) => Promise<Registrant>;
  onUpdateRegistrant: (registrant: Registrant) => void;
  currentUserLabel: string;
  showToast: (type: ToastType, title: string, message?: string) => void;
}

const EMPTY_TRIP_PARTICIPANTS: TripParticipant[] = [];

type DashboardTab = 'registrations' | 'events' | 'inventory' | 'outlook-records' | 'settings';

const Dashboard: React.FC<DashboardProps> = ({
  registrations,
  historicalData,
  tripParticipants = EMPTY_TRIP_PARTICIPANTS,
  watchlist,
  events,
  isEventDataLoading = false,
  systemYear,
  onUpdateSystemYear,
  onUpdateHistoricalData,
  onUpdateWatchlist,
  onUpdateEvents,
  onEditEvent,
  onUpdateEventAndRegistrants,
  onCreateRegistrant,
  onUpdateRegistrant,
  currentUserLabel,
  showToast
}) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('registrations');

  // --- Registration State ---
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>(ROLE_FILTER_ALL);
  const [filterTask, setFilterTask] = useState<string>(TASK_FILTER_ALL);
  const [registrantSort, setRegistrantSort] = useState<RegistrantSortMode>(readStoredRegistrantSort);
  const [selectedForBatch, setSelectedForBatch] = useState<Set<string>>(new Set());
  const [assignmentMode, setAssignmentMode] = useState(false);
  const [batchAssignmentLeadership, setBatchAssignmentLeadership] = useState('');
  const [batchAssignmentTask, setBatchAssignmentTask] = useState('');
  const [batchAssignmentSubDetail, setBatchAssignmentSubDetail] = useState('');
  const [batchAssignmentAssistTarget, setBatchAssignmentAssistTarget] = useState('');
  const [batchAssignmentNote, setBatchAssignmentNote] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [showCustomTaskManager, setShowCustomTaskManager] = useState(false);
  const [editingCustomTask, setEditingCustomTask] = useState<{ original: string; value: string } | null>(null);
  // 「其他」自訂職位輸入暫存（key = registrant id），送出後存入該梯隊並指派
  const [customTaskInput, setCustomTaskInput] = useState<Record<string, string>>({});
  const [addMemberForm, setAddMemberForm] = useState({
    name: '',
    phone: '',
    church: '',
    gender: '',
    role: '',
    leadershipRole: '',
    taskRole: '',
    customTaskRole: '',
    note: '',
  });
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [editMemberForm, setEditMemberForm] = useState(emptyMemberEditForm);

  useEffect(() => {
    try {
      window.localStorage.setItem(REGISTRANT_SORT_STORAGE_KEY, registrantSort);
    } catch {
      // 無痕模式或封鎖儲存時忽略，僅本次有效
    }
  }, [registrantSort]);

  // Clear batch selection when filter changes
  useEffect(() => {
    setSelectedForBatch(new Set());
  }, [filterRole, filterTask, selectedEventId, searchTerm]);

  useEffect(() => {
    setFilterTask(TASK_FILTER_ALL);
    setBatchAssignmentLeadership('');
    setBatchAssignmentTask('');
    setBatchAssignmentSubDetail('');
    setBatchAssignmentAssistTarget('');
    setBatchAssignmentNote('');
  }, [selectedEventId]);

  useEffect(() => {
    setEditingCustomTask(null);
  }, [selectedEventId]);

  useEffect(() => {
    if (!taskSupportsSubDetail(batchAssignmentTask)) {
      setBatchAssignmentSubDetail('');
    }
    if (!taskSupportsAssistTarget(batchAssignmentTask)) {
      setBatchAssignmentAssistTarget('');
    }
  }, [batchAssignmentTask]);

  // --- Event Management State ---
  const [newEvent, setNewEvent] = useState({
    dateRange: '',
    location: '',
    deadline: '',
    deadlineDate: '', // ISO format for backend
    cancelDeadline: '',
    cancelDeadlineDate: '', // 線上取消截止日 ISO（預設出隊前一個月）
    startDate: '',
    capacity: '', // 人數上限（空 = 不限）
    type: 'mission' as EventType
  });
  // 管理員手動改過截止日後，就不再自動帶入預設值
  const [cutoffTouched, setCutoffTouched] = useState({ deadline: false, cancel: false });

  // Date input helpers for better UX
  const [dateInput, setDateInput] = useState({
    month: '',
    startDay: '',
    endDay: '',
    deadlineMonth: '',
    deadlineDay: '',
    cancelMonth: '',
    cancelDay: ''
  });

  // Editing State
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EventOption | null>(null);

  // --- Admin Management State ---
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [inventoryEmails, setInventoryEmails] = useState<string[]>([]);
  const [newInventoryEmail, setNewInventoryEmail] = useState('');
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // --- Year Settings State ---
  const [yearInput, setYearInput] = useState(systemYear.toString());

  // --- Dashboard Year Selector (independent from global systemYear) ---
  const [dashboardYear, setDashboardYear] = useState<number>(systemYear);

  const availableYears = useMemo(() => {
    const years = new Set(events.map(e => e.year || 2025));
    return Array.from(years).sort((a, b) => b - a);
  }, [events]);

  const dashboardEvents = useMemo(() =>
    events
      .filter(e => (e.year || 2025) === dashboardYear)
      .sort(compareEventsChronologically),
    [events, dashboardYear]
  );

  useEffect(() => {
    if (!availableYears.includes(dashboardYear) && availableYears.length > 0) {
      setDashboardYear(availableYears[0]);
    }
  }, [availableYears]);

  useEffect(() => {
    if (dashboardEvents.length > 0 && !selectedEventId) {
      setSelectedEventId(dashboardEvents[0].id);
    } else if (dashboardEvents.length === 0) {
      setSelectedEventId('');
    }
  }, [dashboardEvents, selectedEventId]);

  // --- Watchlist Management State ---
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [newWatchlistChurch, setNewWatchlistChurch] = useState('');
  const [loadingWatchlist, setLoadingWatchlist] = useState(false);

  useEffect(() => {
    if (activeTab === 'settings') {
      setLoadingAdmins(true);
      setLoadingWatchlist(true);
      Promise.all([
        getAllowedAdmins().then(setAdminEmails),
        getAllowedInventoryUsers().then(setInventoryEmails),
        getWatchlist().then(setWatchlistItems)
      ]).finally(() => {
        setLoadingAdmins(false);
        setLoadingWatchlist(false);
      });
    }
  }, [activeTab]);

  const normalizeAccessEmail = (email: string) => email.trim().toLowerCase();

  const handleAddAdmin = async () => {
    const emailToAdd = normalizeAccessEmail(newAdminEmail);
    if (!emailToAdd) return;
    if (adminEmails.includes(emailToAdd)) {
      showToast('info', '已存在', '此 Email 已在管理員名單中');
      return;
    }

    const updatedList = [...adminEmails, emailToAdd];
    setAdminEmails(updatedList);
    setNewAdminEmail('');

    try {
      await updateAllowedAdmins(updatedList);
      showToast('success', '新增成功', `已賦予 ${emailToAdd} 管理權限`);
    } catch (e) {
      showToast('error', '儲存失敗', '無法更新權限設定');
      console.error(e);
    }
  };

  const handleRemoveAdmin = async (emailToRemove: string) => {
    if (window.confirm(`確定要移除 ${emailToRemove} 的管理權限嗎？`)) {
      const updatedList = adminEmails.filter(email => email !== emailToRemove);
      setAdminEmails(updatedList);

      try {
        await updateAllowedAdmins(updatedList);
        showToast('success', '移除成功', '已更新管理權限');
      } catch (e) {
        showToast('error', '儲存失敗', '無法更新權限設定');
        console.error(e);
      }
    }
  };

  const handleAddInventoryUser = async () => {
    const emailToAdd = normalizeAccessEmail(newInventoryEmail);
    if (!emailToAdd) return;
    if (inventoryEmails.includes(emailToAdd)) {
      showToast('info', '已存在', '此 Email 已在物資填寫人員名單中');
      return;
    }

    const updatedList = [...inventoryEmails, emailToAdd];
    setInventoryEmails(updatedList);
    setNewInventoryEmail('');

    try {
      await updateAllowedInventoryUsers(updatedList);
      showToast('success', '新增成功', `已開放 ${emailToAdd} 使用物資管理`);
    } catch (e) {
      showToast('error', '儲存失敗', '無法更新物資填寫人員設定');
      console.error(e);
    }
  };

  const handleRemoveInventoryUser = async (emailToRemove: string) => {
    if (window.confirm(`確定要移除 ${emailToRemove} 的物資管理權限嗎？`)) {
      const updatedList = inventoryEmails.filter(email => email !== emailToRemove);
      setInventoryEmails(updatedList);

      try {
        await updateAllowedInventoryUsers(updatedList);
        showToast('success', '移除成功', '已更新物資填寫人員設定');
      } catch (e) {
        showToast('error', '儲存失敗', '無法更新物資填寫人員設定');
        console.error(e);
      }
    }
  };

  const handleAddWatchlistItem = async () => {
    if (!newWatchlistName || !newWatchlistChurch) {
      showToast('info', '資料不完整', '請同時輸入姓名和教會');
      return;
    }

    // Check if already exists
    const exists = watchlistItems.some(
      item => item.name === newWatchlistName && item.church === newWatchlistChurch
    );

    if (exists) {
      showToast('info', '已存在', '此人員已在觀察名單中');
      return;
    }

    const newItem: WatchlistItem = {
      id: `watchlist_${Date.now()}`,
      name: newWatchlistName,
      church: newWatchlistChurch,
      addedDate: new Date().toISOString()
    };

    const updatedList = [...watchlistItems, newItem];
    setWatchlistItems(updatedList);
    setNewWatchlistName('');
    setNewWatchlistChurch('');

    try {
      await updateWatchlist(updatedList);
      onUpdateWatchlist(updatedList);
      showToast('success', '新增成功', `已將 ${newWatchlistName} (${newWatchlistChurch}) 加入觀察名單`);
    } catch (e) {
      showToast('error', '儲存失敗', '無法更新觀察名單');
      console.error(e);
    }
  };

  const handleRemoveWatchlistItem = async (itemToRemove: WatchlistItem) => {
    if (window.confirm(`確定要將 ${itemToRemove.name} (${itemToRemove.church}) 從觀察名單中移除嗎？`)) {
      const updatedList = watchlistItems.filter(item => item.id !== itemToRemove.id);
      setWatchlistItems(updatedList);

      try {
        await updateWatchlist(updatedList);
        onUpdateWatchlist(updatedList);
        showToast('success', '移除成功', '已更新觀察名單');
      } catch (e) {
        showToast('error', '儲存失敗', '無法更新觀察名單');
        console.error(e);
      }
    }
  };

  // --- Logic: Process Data for Selected Event ---
  const selectedEvent = dashboardEvents.find(e => e.id === selectedEventId);
  const selectedEventRequiresOfficialSupport = selectedEvent ? isOfficialSupportEvent(selectedEvent) : false;
  const shouldDisplayOfficialSupport = (registrant: Registrant): boolean =>
    selectedEventRequiresOfficialSupport && requiresOfficialSupportDisplay(registrant);

  const getOfficialSupportStatus = (registrant: Registrant) => {
    if (registrant.canProvideOfficialSupport === true) {
      const detail = selectedEvent
        ? formatOfficialSupportAvailability(
            selectedEvent,
            registrant.officialSupportAvailability?.[selectedEvent.id] || []
          )
        : '';

      return {
        type: 'yes' as const,
        label: '可報備',
        detail
      };
    }

    if (registrant.canProvideOfficialSupport === false) {
      return {
        type: 'no' as const,
        label: '無報備',
        detail: ''
      };
    }

    return {
      type: 'none' as const,
      label: '-',
      detail: ''
    };
  };

  const taskOptions = useMemo(() => {
    const custom = selectedEvent?.customTaskRoles ?? [];
    const assignedCustom = selectedEventId
      ? registrations
        .map(reg => reg.assignments?.[selectedEventId]?.taskRole)
        .filter((role): role is string => Boolean(role && !TASK_ROLES.includes(role)))
      : [];
    const base = TASK_ROLES.filter(r => r !== '其他');
    const merged = [
      ...base,
      ...[...custom, ...assignedCustom].filter((role, index, arr) =>
        role &&
        role !== '其他' &&
        role !== '尚未分配' &&
        !base.includes(role) &&
        arr.indexOf(role) === index
      )
    ];
    return [...merged, '其他'];
  }, [selectedEvent?.customTaskRoles, selectedEventId, registrations]);

  const customTaskRolesForManagement = useMemo(
    () => taskOptions.filter(role => !TASK_ROLES.includes(role)),
    [taskOptions]
  );

  const stationTargetOptions = useMemo(() => {
    const excluded = new Set(['尚未分配', '其他', '跟診', '流動', '牙科跟診/牙助', '牙助', '牙醫助理']);
    return [
      ...WORK_ASSIGNMENT_STATION_TARGETS,
      ...customTaskRolesForManagement
        .filter(role => !excluded.has(role))
        .map(role => normalizeWorkStation(role))
    ].filter((station, index, arr) => station && arr.indexOf(station) === index);
  }, [customTaskRolesForManagement]);

  const customTaskUsageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    customTaskRolesForManagement.forEach(role => {
      counts[role] = registrations.filter(reg =>
        selectedEventId &&
        reg.selectedEvents.includes(selectedEventId) &&
        reg.assignments?.[selectedEventId]?.taskRole === role
      ).length;
    });
    return counts;
  }, [customTaskRolesForManagement, registrations, selectedEventId]);

  // 第一次參與：姓名索引只在資料變動時重建一次，每位報名者 O(1) 查詢
  const firstTimeIndex = useMemo(
    () => buildFirstTimeParticipantIndex(historicalData, tripParticipants, registrations, events),
    [historicalData, tripParticipants, registrations, events]
  );
  // 動員紀錄尚未載入前不顯示「第一次參與」，避免整梯誤標
  const isFirstTimeDataReady = historicalData.length > 0;

  const processedData = useMemo(() => {
    const eventRegistrants = registrations.filter(reg =>
      selectedEventId ? reg.selectedEvents.includes(selectedEventId) : false
    );
    const eventForFirstTime = events.find(e => e.id === selectedEventId);

    return eventRegistrants.map(reg => {
      const verification = checkEligibility(reg, historicalData, watchlist, { maxYear: systemYear });
      const isFirstTime = isFirstTimeDataReady
        ? isFirstTimeParticipantWithIndex(reg, eventForFirstTime, firstTimeIndex)
        : false;
      const assignment = reg.assignments?.[selectedEventId] || {};

      // Check if user is on watchlist - match both name and church
      const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();
      const isWatchlisted = watchlist.some(item =>
        normalize(item.name) === normalize(reg.name) && normalize(item.church) === normalize(reg.church)
      );

      return {
        ...reg,
        isEligible: verification.isEligible,
        isWatchlisted, // Flag for UI
        isFirstTime,
        matchDetails: verification.matches,
        matchType: verification.matchType,
        currentLeadership: assignment.leadershipRole || '無',
        currentTask: assignment.taskRole || '尚未分配',
        currentSubTask: assignment.taskSubDetail || '',
        currentAssistTarget: assignment.assistTarget || ''
      };
    });
  }, [registrations, historicalData, watchlist, selectedEventId, events, firstTimeIndex, isFirstTimeDataReady]);

  const firstTimeCount = useMemo(
    () => processedData.filter(item => item.isFirstTime).length,
    [processedData]
  );

  // --- Stats Calculation for Gap Analysis ---
  const eventStats = useMemo(() => {
    const stats = {
      westMed: 0,
      tcm: 0,
      dentist: 0,
      nurse: 0,
      pharmacist: 0,
      physio: 0,
      unassignedTask: 0,
      total: processedData.length
    };

    processedData.forEach(r => {
      // Check registered Role string (支援多選角色)
      const allRoles = r.roles?.join(' ') || r.role || '';
      if (allRoles.includes('西醫')) stats.westMed++;
      if (allRoles.includes('中醫')) stats.tcm++;
      if (allRoles.includes('牙醫')) stats.dentist++;
      if (allRoles.includes('護理')) stats.nurse++;
      if (allRoles.includes('藥師')) stats.pharmacist++;
      if (allRoles.includes('物理治療師') || (allRoles.includes('治療師') && r.therapistSpecialty === '物理治療師')) {
        stats.physio++;
      }
      if (r.currentTask === '尚未分配') stats.unassignedTask++;
    });

    return stats;
  }, [processedData]);

  const taskFilterOptions = useMemo(() => {
    const ordered = taskOptions.filter(role => role !== '其他');
    const discovered = processedData
      .map(item => getTaskFilterKey(item.currentTask, item.currentSubTask))
      .filter(task => task && task !== '其他');
    return Array.from(new Set([...ordered, ...discovered]));
  }, [processedData, taskOptions]);

  const taskFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    processedData.forEach(item => {
      const task = getTaskFilterKey(item.currentTask, item.currentSubTask);
      counts[task] = (counts[task] || 0) + 1;
    });
    return counts;
  }, [processedData]);

  const roleFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    processedData.forEach(item => {
      getRegistrantRoleKeys(item).forEach(role => {
        counts[role] = (counts[role] || 0) + 1;
      });
    });
    return counts;
  }, [processedData]);

  const roleFilterOptions = useMemo(
    () => Object.keys(roleFilterCounts).sort(sortRoleFilterOptions),
    [roleFilterCounts]
  );

  useEffect(() => {
    if (filterRole !== ROLE_FILTER_ALL && !roleFilterOptions.includes(filterRole)) {
      setFilterRole(ROLE_FILTER_ALL);
    }
  }, [filterRole, roleFilterOptions]);

  const assignmentStats = useMemo(() => {
    const taskAssigned = processedData.filter(item => getTaskFilterKey(item.currentTask, item.currentSubTask) !== '尚未分配').length;
    const leadershipAssigned = processedData.filter(item => item.currentLeadership && item.currentLeadership !== '無').length;
    const pendingTask = processedData.length - taskAssigned;
    const pendingApproval = processedData.filter(item => !item.isApproved).length;

    return {
      taskAssigned,
      pendingTask,
      leadershipAssigned,
      pendingApproval,
      total: processedData.length
    };
  }, [processedData]);

  // --- Approved count per event (for capacity checking) ---
  const approvedCountByEvent = useMemo(() => {
    const counts: Record<string, number> = {};
    dashboardEvents.forEach(evt => {
      counts[evt.id] = registrations.filter(
        r => r.selectedEvents.includes(evt.id) && r.isApproved
      ).length;
    });
    return counts;
  }, [registrations, dashboardEvents]);

  const filteredData = filteredDataHelper(processedData, searchTerm, filterRole, filterTask, registrantSort);

  const getHistoricalDetailText = (registrant: typeof processedData[number]) => {
    const years = Array.from(new Set(
      registrant.matchDetails
        .map(match => match.yearAttended)
        .filter(year => year > 0 && year <= systemYear)
    )).sort((a, b) => b - a);

    if (years.length > 0) {
      return `動員年份：${years.join('、')}`;
    }

    return registrant.matchDetails.length > 0 ? '有歷史資料，年份未詳' : '';
  };

  const renderHistoryBadge = (registrant: typeof processedData[number]) => {
    if (registrant.isWatchlisted) {
      return (
        <div className="mt-1 inline-flex max-w-full items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-bold text-orange-700 border border-orange-100">
          <ShieldAlert size={13} className="mr-1 shrink-0" />
          <span className="truncate">觀察名單，需人工審核</span>
        </div>
      );
    }

    if (registrant.matchDetails.length > 0) {
      const detailText = getHistoricalDetailText(registrant);
      return (
        <details className="mt-1 inline-block max-w-full">
          <summary className="inline-flex max-w-full cursor-pointer select-none items-center rounded-md bg-green-50 px-2 py-1 text-xs font-bold text-green-700 border border-green-100 hover:bg-green-100">
            <CheckCircle size={13} className="mr-1 shrink-0" />
            <span className="truncate">有動員紀錄</span>
          </summary>
          <div className="mt-1 rounded-md border border-green-100 bg-white px-2 py-1 text-xs font-medium leading-5 text-green-800 shadow-sm">
            {detailText}
          </div>
        </details>
      );
    }

    if (registrant.isVerifiedOverride) {
      return (
        <div className="mt-1 inline-flex max-w-full items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700 border border-indigo-100">
          <UserCheck size={13} className="mr-1 shrink-0" />
          <span className="truncate">手動覆核合格</span>
        </div>
      );
    }

    return (
      <div className="mt-1 inline-flex max-w-full items-center rounded-md bg-red-50 px-2 py-1 text-xs font-bold text-red-600 border border-red-100">
        <ShieldAlert size={13} className="mr-1 shrink-0" />
        <span className="truncate">未找到動員紀錄</span>
      </div>
    );
  };

  const renderFirstTimeBadge = (registrant: typeof processedData[number]) => {
    if (!registrant.isFirstTime) return null;
    return (
      <span
        className="inline-flex shrink-0 items-center rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold leading-4 text-violet-700 whitespace-nowrap"
        title={FIRST_TIME_PARTICIPANT_TOOLTIP}
      >
        <Sparkles size={11} className="mr-0.5 shrink-0" />
        第一次參與
      </span>
    );
  };

  const renderOfficialSupportInline = (registrant: typeof processedData[number]) => {
    if (!shouldDisplayOfficialSupport(registrant)) return null;

    const status = getOfficialSupportStatus(registrant);
    const statusClass =
      status.type === 'yes'
        ? 'bg-green-50 text-green-800 border-green-100'
        : status.type === 'no'
          ? 'bg-gray-50 text-gray-700 border-gray-200'
          : 'bg-amber-50 text-amber-800 border-amber-100';

    const label = status.type === 'none' ? '未填' : status.label;

    return (
      <div className={`mt-2 max-w-full rounded-md border px-2 py-1 text-xs font-bold leading-5 ${statusClass}`}>
        <span className="whitespace-nowrap">報備支援：{label}</span>
        {status.detail && (
          <span className="ml-1 break-words font-medium">{status.detail}</span>
        )}
      </div>
    );
  };

  const getAssistTargetOptions = (station: string) => {
    const normalizedStation = normalizeWorkStation(station);
    if (!normalizedStation) return [];

    return processedData
      .filter(item => {
        const task = getTaskFilterKey(item.currentTask, item.currentSubTask);
        if (task === '尚未分配' || task === '流動' || task === '跟診' || isDentalAssistantTask(task)) {
          return false;
        }
        return normalizeWorkStation(task) === normalizedStation;
      })
      .map(item => item.name)
      .filter((name, index, arr) => arr.indexOf(name) === index);
  };

  const getAssistStationForRegistrant = (registrant: typeof processedData[number]) => {
    if (isDentalAssistantTask(registrant.currentTask)) return '牙科';
    if (registrant.currentTask === '跟診') return normalizeWorkStation(registrant.currentSubTask);
    return '';
  };

  const getAssistTargetSelectOptions = (registrant: typeof processedData[number]) => {
    const options = getAssistTargetOptions(getAssistStationForRegistrant(registrant));
    if (registrant.currentAssistTarget && !options.includes(registrant.currentAssistTarget)) {
      return [...options, registrant.currentAssistTarget];
    }
    return options;
  };

  useEffect(() => {
    if (!batchAssignmentAssistTarget) return;
    const station = isDentalAssistantTask(batchAssignmentTask) ? '牙科' : batchAssignmentSubDetail;
    if (!getAssistTargetOptions(station).includes(batchAssignmentAssistTarget)) {
      setBatchAssignmentAssistTarget('');
    }
  }, [batchAssignmentAssistTarget, batchAssignmentSubDetail, batchAssignmentTask]);

  function filteredDataHelper(data: typeof processedData, term: string, role: string, task: string, sortMode: RegistrantSortMode) {
    const filtered = data.filter(item => {
      const matchesSearch =
        item.name.toLowerCase().includes(term.toLowerCase()) ||
        item.church.toLowerCase().includes(term.toLowerCase()) ||
        item.email.toLowerCase().includes(term.toLowerCase());
      const matchesRole = role === ROLE_FILTER_ALL || getRegistrantRoleKeys(item).includes(role);
      const matchesTask = task === TASK_FILTER_ALL || getTaskFilterKey(item.currentTask, item.currentSubTask) === task;

      return matchesSearch && matchesRole && matchesTask;
    });

    const byName = (a: typeof processedData[number], b: typeof processedData[number]) =>
      a.name.localeCompare(b.name, 'zh-Hant');

    const bySubmission = (direction: 1 | -1) =>
      (a: typeof processedData[number], b: typeof processedData[number]) => {
        const timeA = getSubmissionTimestamp(a.submissionDate);
        const timeB = getSubmissionTimestamp(b.submissionDate);
        if (timeA === null || timeB === null) {
          if (timeA !== timeB) return timeA === null ? 1 : -1; // 缺漏／無效日期放最後
          return byName(a, b);
        }
        return (timeA - timeB) * direction || byName(a, b);
      };

    switch (sortMode) {
      case 'submitted-asc':
        return filtered.sort(bySubmission(1));
      case 'submitted-desc':
        return filtered.sort(bySubmission(-1));
      case 'name':
        return filtered.sort(byName);
      case 'church':
        return filtered.sort((a, b) => {
          const churchA = (a.church || '').trim();
          const churchB = (b.church || '').trim();
          if (!churchA || !churchB) {
            if (churchA !== churchB) return churchA ? -1 : 1; // 未填教會放最後
            return byName(a, b);
          }
          return churchA.localeCompare(churchB, 'zh-Hant') || byName(a, b);
        });
      case 'first-time':
        return filtered.sort((a, b) => {
          if (a.isFirstTime !== b.isFirstTime) return a.isFirstTime ? -1 : 1;
          return byName(a, b);
        });
      case 'default':
      default:
        return filtered.sort((a, b) => {
          // Prioritize Pending approvals
          if (a.isApproved !== b.isApproved) {
            return a.isApproved ? 1 : -1; // Pending first
          }
          // Then check eligibility
          if (a.isEligible !== b.isEligible) {
            return a.isEligible ? 1 : -1;
          }
          return a.name.localeCompare(b.name);
        });
    }
  }

  // --- Handlers ---
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        const parsed = parseHistoricalCSV(text, { maxYear: systemYear });
        if (parsed.length > 0) {
          onUpdateHistoricalData(parsed);
          // Success toast is handled by parent
        } else {
          showToast('error', '匯入失敗', '請確認 CSV 格式是否正確');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClearHistoricalData = async () => {
    const confirmed = window.confirm(
      `⚠️ 警告：此操作將刪除系統內所有 ${historicalData.length} 筆歷史資料！\n\n` +
      `這個操作無法復原，建議您先下載備份。\n\n` +
      `確定要繼續嗎？`
    );

    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      `🚨 最後確認：您真的要刪除全部 ${historicalData.length} 筆資料嗎？\n\n` +
      `刪除後將無法恢復，請確保您有備份。`
    );

    if (!doubleConfirm) return;

    try {
      const deletedCount = await clearAllHistoricalData();
      onUpdateHistoricalData([]);
      showToast('success', '清空成功', `已刪除 ${deletedCount} 筆歷史資料`);
    } catch (error) {
      console.error('Error clearing historical data:', error);
      showToast('error', '清空失敗', '無法刪除歷史資料，請稍後再試');
    }
  };

  const closeAddMemberModal = () => {
    setShowAddMemberModal(false);
    setAddMemberForm({ name: '', phone: '', church: '', gender: '', role: '', leadershipRole: '', taskRole: '', customTaskRole: '', note: '' });
  };

  const startEditMember = (registrant: Registrant) => {
    setEditMemberForm({
      id: registrant.id,
      name: registrant.name || '',
      email: registrant.email || '',
      phone: registrant.phone || '',
      church: registrant.church || '',
      gender: registrant.gender || '',
      role: registrant.role || registrant.roles?.[0] || '',
      otherRoleDescription: registrant.otherRoleDescription || '',
      studentSpecialization: registrant.studentSpecialization || '',
      westernMedicineSpecialty: registrant.westernMedicineSpecialty || '',
      therapistSpecialty: registrant.therapistSpecialty || '',
      message: registrant.message || '',
    });
    setShowEditMemberModal(true);
  };

  const closeEditMemberModal = () => {
    setShowEditMemberModal(false);
    setEditMemberForm(emptyMemberEditForm);
  };

  const handleUpdateMemberProfile = async () => {
    const registrant = registrations.find(r => r.id === editMemberForm.id);
    if (!registrant) {
      showToast('error', '找不到資料', '此成員可能已被刪除，請重新整理後再試');
      return;
    }
    if (!editMemberForm.name.trim()) {
      showToast('error', '資料不完整', '姓名不可空白');
      return;
    }

    let name = '';
    let phone = '';
    let church = '';
    let role = '';
    let otherRoleDescription = '';
    let studentSpecialization = '';
    let westernMedicineSpecialty = '';
    let therapistSpecialty = '';
    let message = '';
    const email = editMemberForm.email.trim().toLowerCase();

    if (email && !email.includes('@')) {
      showToast('error', 'Email 格式錯誤', '請輸入有效的 Email，或留空');
      return;
    }

    try {
      name = sanitizeName(editMemberForm.name);
      phone = editMemberForm.phone.trim() ? sanitizePhone(editMemberForm.phone) : '';
      church = editMemberForm.church.trim() ? sanitizeChurch(editMemberForm.church) : '';
      role = sanitizeText(editMemberForm.role.trim(), 100);
      otherRoleDescription = sanitizeText(editMemberForm.otherRoleDescription.trim(), 100);
      studentSpecialization = sanitizeText(editMemberForm.studentSpecialization.trim(), 100);
      westernMedicineSpecialty = sanitizeText(editMemberForm.westernMedicineSpecialty.trim(), 100);
      therapistSpecialty = sanitizeText(editMemberForm.therapistSpecialty.trim(), 100);
      message = sanitizeText(editMemberForm.message.trim(), 500);
    } catch (error) {
      showToast('error', '資料格式錯誤', error instanceof Error ? error.message : '請檢查輸入內容');
      return;
    }

    setIsUpdatingMember(true);
    try {
      onUpdateRegistrant({
        ...registrant,
        name,
        email,
        phone,
        church,
        gender: (editMemberForm.gender || '') as Gender,
        role,
        roles: role ? [role] : [],
        otherRoleDescription: otherRoleDescription || undefined,
        studentSpecialization: studentSpecialization || undefined,
        westernMedicineSpecialty: westernMedicineSpecialty || undefined,
        therapistSpecialty: therapistSpecialty || undefined,
        message,
      });
      closeEditMemberModal();
    } finally {
      setIsUpdatingMember(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedEventId) {
      showToast('error', '請先選擇梯隊', '請於上方選擇要新增成員的梯隊');
      return;
    }
    if (!addMemberForm.name.trim()) {
      showToast('error', '資料不完整', '請至少填寫姓名');
      return;
    }
    if (addMemberForm.taskRole === '其他' && !addMemberForm.customTaskRole.trim()) {
      showToast('error', '資料不完整', '請輸入自訂工作名稱');
      return;
    }

    let name = '';
    let phone = '';
    let church = '';
    let sanitizedRole = '';
    let resolvedTaskRole = addMemberForm.taskRole;
    let sanitizedNote = '';

    try {
      name = sanitizeName(addMemberForm.name);
      phone = sanitizePhone(addMemberForm.phone);
      church = addMemberForm.church.trim() ? sanitizeChurch(addMemberForm.church) : '';
      sanitizedRole = sanitizeText(addMemberForm.role.trim(), 100);
      if (addMemberForm.taskRole === '其他') {
        resolvedTaskRole = sanitizeText(addMemberForm.customTaskRole, 50);
      }
      sanitizedNote = sanitizeText(addMemberForm.note.trim(), 500);
    } catch (error) {
      showToast('error', '資料格式錯誤', error instanceof Error ? error.message : '請檢查輸入內容');
      return;
    }

    // 輕量重複提醒：姓名+電話已存在則警告，但不硬擋
    if (phone) {
      const dup = registrations.some(
        r => r.name === name && normalizePhone(r.phone || '') === normalizePhone(phone)
      );
      if (dup && !window.confirm(`系統已有相同「姓名+電話」的記錄（${name}）。\n仍要新增嗎？`)) {
        return;
      }
    }

    setIsAddingMember(true);
    try {
      if (selectedEvent && resolvedTaskRole && !TASK_ROLES.includes(resolvedTaskRole)) {
        const existing = selectedEvent.customTaskRoles ?? [];
        if (!existing.includes(resolvedTaskRole)) {
          onEditEvent({ ...selectedEvent, customTaskRoles: [...existing, resolvedTaskRole] });
        }
      }

      const eventAssignment = {
        ...(addMemberForm.leadershipRole ? { leadershipRole: addMemberForm.leadershipRole } : {}),
        ...(resolvedTaskRole ? { taskRole: resolvedTaskRole } : {}),
        ...(sanitizedNote ? { note: sanitizedNote } : {}),
      };

      await onCreateRegistrant({
        email: '',
        name,
        gender: (addMemberForm.gender || '') as Gender,
        church,
        district: '' as District,
        phone,
        message: '',
        role: sanitizedRole,
        roles: sanitizedRole ? [sanitizedRole] : [],
        selectedEvents: [selectedEventId],
        assignments: {
          [selectedEventId]: eventAssignment,
        },
        submissionDate: new Date().toISOString(),
        isApproved: true,
        source: 'manual',
      } as Omit<Registrant, 'id'>);

      showToast('success', '新增成功', `已將 ${name} 加入此梯隊`);
      setShowAddMemberModal(false);
      setAddMemberForm({ name: '', phone: '', church: '', gender: '', role: '', leadershipRole: '', taskRole: '', customTaskRole: '', note: '' });
    } catch (error) {
      console.error('Error adding manual member:', error);
      showToast('error', '新增失敗', '無法新增成員，請稍後再試');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleAssignmentChange = (id: string, field: 'leadershipRole' | 'taskRole' | 'taskSubDetail' | 'assistTarget' | 'note', value: string) => {
    const registrant = registrations.find(r => r.id === id);
    if (!registrant) return;
    const current = registrant.assignments || {};
    const eventAssignment = current[selectedEventId] || {};

    const nextValue = field === 'note'
      ? sanitizeText(value, 120)
      : field === 'assistTarget'
        ? sanitizeText(value, 80)
        : value;
    let updatedAssignment = { ...eventAssignment, [field]: nextValue };

    if (field === 'taskRole' && !taskSupportsSubDetail(value)) {
      updatedAssignment.taskSubDetail = '';
    }

    if (field === 'taskRole' && !taskSupportsAssistTarget(value)) {
      updatedAssignment.assistTarget = '';
    }

    onUpdateRegistrant({
      ...registrant,
      assignments: {
        ...current,
        [selectedEventId]: updatedAssignment
      }
    });
  };

  const handleBatchAssignmentApply = async () => {
    if (!selectedEvent) return;
    if (selectedForBatch.size === 0) {
      showToast('info', '請先勾選人員', '可先搜尋或篩選，再勾選要套用分工的人員。');
      return;
    }

    const cleanNote = sanitizeText(batchAssignmentNote.trim(), 120);
    const cleanSubDetail = taskSupportsSubDetail(batchAssignmentTask)
      ? sanitizeText(batchAssignmentSubDetail.trim(), 50)
      : '';
    const cleanAssistTarget = taskSupportsAssistTarget(batchAssignmentTask)
      ? sanitizeText(batchAssignmentAssistTarget.trim(), 80)
      : '';
    const hasChange = Boolean(batchAssignmentLeadership || batchAssignmentTask || cleanSubDetail || cleanAssistTarget || cleanNote);
    if (!hasChange) {
      showToast('info', '尚未選擇分工', '請至少選擇職務、工作，或輸入分工備註。');
      return;
    }

    const selectedRegistrants = Array.from(selectedForBatch)
      .map(id => registrations.find(r => r.id === id))
      .filter((r): r is Registrant => Boolean(r && r.selectedEvents.includes(selectedEventId)));

    if (selectedRegistrants.length === 0) {
      showToast('info', '沒有可更新的人員', '目前勾選的人員不屬於此梯隊。');
      return;
    }

    const updatedRegistrants = selectedRegistrants.map(registrant => {
      const current = registrant.assignments || {};
      const eventAssignment = current[selectedEventId] || {};
      const nextAssignment = { ...eventAssignment };

      if (batchAssignmentLeadership) {
        nextAssignment.leadershipRole = batchAssignmentLeadership;
      }

      if (batchAssignmentTask) {
        nextAssignment.taskRole = batchAssignmentTask;
        nextAssignment.taskSubDetail = taskSupportsSubDetail(batchAssignmentTask) ? cleanSubDetail : '';
        nextAssignment.assistTarget = taskSupportsAssistTarget(batchAssignmentTask) ? cleanAssistTarget : '';
      }

      if (cleanNote) {
        nextAssignment.note = cleanNote;
      }

      return {
        ...registrant,
        assignments: {
          ...current,
          [selectedEventId]: nextAssignment
        }
      };
    });

    try {
      await onUpdateEventAndRegistrants(
        selectedEvent,
        updatedRegistrants,
        '批次分工成功',
        `已更新 ${updatedRegistrants.length} 位人員的工作安排`
      );
      setSelectedForBatch(new Set());
      setBatchAssignmentLeadership('');
      setBatchAssignmentTask('');
      setBatchAssignmentSubDetail('');
      setBatchAssignmentAssistTarget('');
      setBatchAssignmentNote('');
    } catch (error) {
      console.error('Batch assignment error:', error);
    }
  };

  // 送出「其他」自訂職位：併入該梯隊的 customTaskRoles（下次下拉就有），並直接指派給該隊員
  const handleAddCustomTask = (registrantId: string) => {
    if (!selectedEvent) return;
    const registrant = registrations.find(r => r.id === registrantId);
    const fallback = registrant?.assignments?.[selectedEventId]?.taskSubDetail || '';
    const value = sanitizeText((customTaskInput[registrantId] ?? fallback).trim(), 50);
    if (!value) {
      showToast('error', '資料不完整', '請輸入自訂工作名稱');
      return;
    }
    if (value === '其他' || value === '尚未分配') {
      showToast('error', '名稱不可使用', '請輸入明確的自訂工作名稱');
      return;
    }

    // 1. 若是新職位，存入該梯隊的自訂清單
    const existing = selectedEvent.customTaskRoles ?? [];
    const isKnown = TASK_ROLES.includes(value) || existing.includes(value);
    if (!isKnown) {
      onEditEvent({ ...selectedEvent, customTaskRoles: [...existing, value] });
    }

    // 2. 直接把該隊員指派到此自訂職位（taskRole 變成正式選項值）
    if (registrant) {
      const current = registrant.assignments || {};
      const eventAssignment = current[selectedEventId] || {};
      onUpdateRegistrant({
        ...registrant,
        assignments: {
          ...current,
          [selectedEventId]: { ...eventAssignment, taskRole: value, taskSubDetail: '', assistTarget: '' }
        }
      });
    }

    // 3. 清掉輸入暫存
    setCustomTaskInput(prev => {
      const next = { ...prev };
      delete next[registrantId];
      return next;
    });
  };

  const buildRegistrantsWithCustomTaskChange = (originalRole: string, nextRole?: string): Registrant[] => {
    if (!selectedEventId) return [];

    return registrations
      .filter(reg =>
        reg.selectedEvents.includes(selectedEventId) &&
        reg.assignments?.[selectedEventId]?.taskRole === originalRole
      )
      .map(reg => {
        const current = reg.assignments || {};
        const eventAssignment = current[selectedEventId] || {};

        if (nextRole) {
          return {
            ...reg,
            assignments: {
              ...current,
              [selectedEventId]: {
                ...eventAssignment,
                taskRole: nextRole,
                taskSubDetail: '',
                assistTarget: ''
              }
            }
          };
        }

        const { taskRole, taskSubDetail, assistTarget, ...remainingAssignment } = eventAssignment;
        void taskRole;
        void taskSubDetail;
        void assistTarget;

        return {
          ...reg,
          assignments: {
            ...current,
            [selectedEventId]: remainingAssignment
          }
        };
      });
  };

  const handleSaveCustomTaskRole = async (originalRole: string) => {
    if (!selectedEvent || !editingCustomTask || editingCustomTask.original !== originalRole) return;

    const nextRole = sanitizeText(editingCustomTask.value, 50);
    if (!nextRole) {
      showToast('error', '資料不完整', '請輸入自訂工作名稱');
      return;
    }
    if (TASK_ROLES.includes(nextRole)) {
      showToast('error', '名稱不可使用', '自訂工作不可與內建工作重複');
      return;
    }
    if (customTaskRolesForManagement.some(role => role !== originalRole && role === nextRole)) {
      showToast('error', '名稱重複', '此自訂工作已存在');
      return;
    }

    if (nextRole === originalRole) {
      setEditingCustomTask(null);
      return;
    }

    const updatedCustomRoles = customTaskRolesForManagement
      .map(role => role === originalRole ? nextRole : role)
      .filter((role, index, arr) => arr.indexOf(role) === index);
    const updatedRegistrants = buildRegistrantsWithCustomTaskChange(originalRole, nextRole);

    try {
      await onUpdateEventAndRegistrants(
        { ...selectedEvent, customTaskRoles: updatedCustomRoles },
        updatedRegistrants,
        '自訂工作已修改',
        `已將「${originalRole}」改為「${nextRole}」，並更新 ${updatedRegistrants.length} 位人員`
      );
      setEditingCustomTask(null);
    } catch (error) {
      console.error('Update custom task role error:', error);
    }
  };

  const handleDeleteCustomTaskRole = async (roleToDelete: string) => {
    if (!selectedEvent) return;

    const usageCount = customTaskUsageCounts[roleToDelete] || 0;
    const confirmed = window.confirm(
      `確定要刪除自訂工作「${roleToDelete}」嗎？\n\n` +
      (usageCount > 0
        ? `目前有 ${usageCount} 位人員使用此工作，刪除後會改為尚未分配。`
        : '目前沒有任何人使用此工作。')
    );

    if (!confirmed) return;

    const updatedCustomRoles = customTaskRolesForManagement.filter(role => role !== roleToDelete);
    const updatedRegistrants = buildRegistrantsWithCustomTaskChange(roleToDelete);

    try {
      await onUpdateEventAndRegistrants(
        { ...selectedEvent, customTaskRoles: updatedCustomRoles },
        updatedRegistrants,
        '自訂工作已刪除',
        `已刪除「${roleToDelete}」${usageCount > 0 ? `，並將 ${usageCount} 位人員改為尚未分配` : ''}`
      );
      if (editingCustomTask?.original === roleToDelete) {
        setEditingCustomTask(null);
      }
    } catch (error) {
      console.error('Delete custom task role error:', error);
    }
  };

  // Check if an event should be auto-closed due to reaching capacity
  const checkAndAutoClose = (eventIds: string[], additionalApprovedCount: Record<string, number> = {}) => {
    eventIds.forEach(eventId => {
      const event = events.find(e => e.id === eventId);
      if (!event?.capacity || event.registrationClosed) return;

      const currentApproved = approvedCountByEvent[eventId] || 0;
      const additional = additionalApprovedCount[eventId] || 0;
      const totalApproved = currentApproved + additional;

      if (totalApproved >= event.capacity) {
        onEditEvent({ ...event, registrationClosed: true });
        showToast('info', '自動額滿關閉', `${event.dateRange} 已達人數上限 ${event.capacity} 人，報名已自動關閉`);
      }
    });
  };

  const handleApprovalToggle = (id: string, currentStatus: boolean | undefined) => {
    const registrant = registrations.find(r => r.id === id);
    if (!registrant) return;

    const newStatus = !currentStatus;

    if (newStatus === true) {
      if (!window.confirm(`確定要將 ${registrant.name} 上架嗎？\n系統將會補發「報名成功確認信」給對方。`)) {
        return;
      }
    }

    onUpdateRegistrant({
      ...registrant,
      isApproved: newStatus
    });

    // Check auto-close when approving
    if (newStatus === true) {
      const additionalCounts: Record<string, number> = {};
      registrant.selectedEvents.forEach(evtId => {
        additionalCounts[evtId] = 1;
      });
      checkAndAutoClose(registrant.selectedEvents, additionalCounts);
    }
  };

  const handleBatchSelection = (id: string, checked: boolean) => {
    setSelectedForBatch(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAllFiltered = (checked: boolean) => {
    if (checked) {
      const ids = new Set(filteredData.map(r => r.id));
      setSelectedForBatch(ids);
    } else {
      setSelectedForBatch(new Set());
    }
  };

  const handleBatchApprove = async () => {
    if (selectedForBatch.size === 0) {
      showToast('info', '請選擇人員', '請先勾選要遴選通過的人員');
      return;
    }

    const confirmed = window.confirm(
      `確定要將已選擇的 ${selectedForBatch.size} 位人員遴選通過嗎？\n\n` +
      `遴選通過後，他們將顯示在公開名單上，並會收到確認信。`
    );

    if (!confirmed) return;

    try {
      // ✅ 收集需要更新的人員清單（只處理未遴選通過的人員）
      const registrantsToApprove = Array.from(selectedForBatch)
        .map(id => registrations.find(r => r.id === id))
        .filter(r => r && !r.isApproved) as Registrant[];

      if (registrantsToApprove.length === 0) {
        showToast('info', '無需更新', '所選人員已全部遴選通過');
        setSelectedForBatch(new Set());
        return;
      }

      // ✅ 使用單一 Firestore Batch 操作（保證原子性：全部成功或全部失敗）
      const batch = writeBatch(db);

      registrantsToApprove.forEach(registrant => {
        const privateRef = doc(db, 'registrations', registrant.id);
        const publicRef = doc(db, 'public_roster', registrant.id);

        // 更新私有記錄
        batch.update(privateRef, {
          isApproved: true,
          lastUpdated: serverTimestamp()
        });

        // 雙寫公開名單（同步到 public_roster 集合）
        const safeData = {
          ...buildPublicRosterPayload(registrant.id, registrant),
          lastUpdated: serverTimestamp(),
        };
        batch.set(publicRef, safeData);
      });

      // ✅ 一次提交所有變更（全部成功或全部失敗）
      await batch.commit();

      // ✅ 只有確認寫入成功後才顯示成功訊息
      showToast('success', '批次遴選成功', `已將 ${registrantsToApprove.length} 位人員遴選通過`);
      setSelectedForBatch(new Set());

      // ✅ 檢查是否有事件達到人數上限，需自動關閉
      const additionalCounts: Record<string, number> = {};
      registrantsToApprove.forEach(r => {
        r.selectedEvents.forEach(evtId => {
          additionalCounts[evtId] = (additionalCounts[evtId] || 0) + 1;
        });
      });
      const affectedEventIds = Object.keys(additionalCounts);
      checkAndAutoClose(affectedEventIds, additionalCounts);

      // ✅ 發送確認信（非阻塞，即使個別信件發送失敗也不影響遴選結果）
      // 使用現有的 sendConfirmationEmail 函數，內容與報名時相同
      registrantsToApprove.forEach(registrant => {
        sendConfirmationEmail(registrant, events).catch(err => {
          console.error(`Failed to send confirmation email to ${registrant.email}:`, err);
        });
      });

    } catch (error) {
      console.error('Batch approval error:', error);
      showToast('error', '批次遴選失敗', '操作失敗，請確認網路連線後重試。所有人員皆未遴選。');
      // ❌ 失敗時不清空選擇，讓用戶可以重試
    }
  };

  const handleRemoveRegistrantFromSelectedEvent = (id: string) => {
    const registrant = registrations.find(r => r.id === id);
    if (!registrant || !selectedEventId) return;

    if (!registrant.selectedEvents.includes(selectedEventId)) {
      showToast('info', '不需移除', `${registrant.name} 不在目前梯次名單中。`);
      return;
    }

    const selectedEventLabel = selectedEvent?.dateRange || '目前梯次';
    const remainingEventCount = registrant.selectedEvents.filter(eventId => eventId !== selectedEventId).length;

    const confirmed = window.confirm(
      `確定要將 ${registrant.name} (${registrant.church}) 從「${selectedEventLabel}」移除嗎？\n\n` +
      `只會移除這個梯次，${remainingEventCount > 0 ? `其他 ${remainingEventCount} 個梯次的報名會保留。` : '原始報名資料會保留，但此人將不再出現在任何梯次名單中。'}`
    );

    if (!confirmed) return;

    const nextAssignments = { ...(registrant.assignments ?? {}) };
    delete nextAssignments[selectedEventId];

    const nextOfficialSupportAvailability = registrant.officialSupportAvailability
      ? { ...registrant.officialSupportAvailability }
      : undefined;
    if (nextOfficialSupportAvailability) {
      delete nextOfficialSupportAvailability[selectedEventId];
    }

    onUpdateRegistrant({
      ...registrant,
      selectedEvents: registrant.selectedEvents.filter(eventId => eventId !== selectedEventId),
      assignments: nextAssignments,
      ...(nextOfficialSupportAvailability ? { officialSupportAvailability: nextOfficialSupportAvailability } : {}),
    });
  };

  const handleManualOverride = (id: string) => {
    const registrant = registrations.find(r => r.id === id);
    if (!registrant) return;

    const isConfirmed = window.confirm(`確定要手動將 ${registrant.name} 標記為「資格符合」嗎？\n(通常用於歷史名單有漏字或錯別字的情況)`);
    if (isConfirmed) {
      onUpdateRegistrant({
        ...registrant,
        isVerifiedOverride: true
      });
    }
  };

  const handleExportRegistrationRoster = () => {
    if (!selectedEvent) return;

    const approvedRegistrants = processedData
      .filter(registrant => registrant.isApproved)
      .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant'));

    if (approvedRegistrants.length === 0) {
      showToast('info', '無可匯出資料', '目前沒有已通過成員可匯出');
      return;
    }

    const escape = (c: string) => `"${String(c ?? '').replace(/"/g, '""')}"`;
    const toLine = (row: string[]) => row.map(escape).join(',');
    const lines = [
      ['姓名', '性別', '教會', '電話', 'Email', '醫宣角色', '第一次參與'],
      ...approvedRegistrants.map(registrant => [
        registrant.name || '',
        registrant.gender || '',
        registrant.church || '',
        registrant.phone || '',
        registrant.email || '',
        formatRosterRoles(registrant),
        registrant.isFirstTime ? '是' : ''
      ])
    ];
    const csvContent = '\uFEFF' + lines.map(toLine).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const safeName = `${selectedEvent.dateRange}_${selectedEvent.location}_報名名冊`.replace(/[\\/:*?"<>|]+/g, '_');
    link.href = url;
    link.download = `${safeName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast('success', '匯出成功', '報名名冊 CSV 已下載');
  };

  const handleExportAssignments = () => {
    if (!selectedEvent) return;

    const eventDateForSheet = formatRocDate(selectedEvent.startDate);
    const sortedData = [...processedData].sort((a, b) => {
      const taskA = getTaskFilterKey(a.currentTask, a.currentSubTask);
      const taskB = getTaskFilterKey(b.currentTask, b.currentSubTask);
      if (taskA !== taskB) return taskA.localeCompare(taskB, 'zh-Hant');
      return a.name.localeCompare(b.name, 'zh-Hant');
    });

    const assignmentNote = (d: typeof processedData[number]) => {
      const note = d.assignments?.[selectedEventId]?.note || '';
      return note.trim();
    };
    const personName = (d: typeof processedData[number]) => {
      const note = assignmentNote(d);
      return note ? `${d.name}（${note}）` : d.name;
    };

    const leadershipLine = LEADERSHIP_ROLES
      .filter(role => role !== '無')
      .map(role => {
        const names = sortedData
          .filter(d => d.currentLeadership === role)
          .map(d => d.name);
        return names.length > 0 ? `${role}：${names.join('、')}` : '';
      })
      .filter(Boolean)
      .join(' / ');

    const leadershipOnly = sortedData
      .filter(d => d.currentLeadership !== '無' && getTaskFilterKey(d.currentTask, d.currentSubTask) === '尚未分配');

    const leadershipOnlyLine = leadershipOnly.length > 0
      ? `隊職未分配工作：${leadershipOnly.map(d => `${d.name}（${d.currentLeadership}）`).join('、')}`
      : '';

    const stationGroups = new Map<string, {
      primary: typeof processedData;
      clinicAssistants: typeof processedData;
      dentalAssistants: typeof processedData;
      floating: typeof processedData;
    }>();
    const ensureStationGroup = (station: string) => {
      const title = normalizeWorkStation(station) || station;
      const existing = stationGroups.get(title);
      if (existing) return existing;

      const next = {
        primary: [] as typeof processedData,
        clinicAssistants: [] as typeof processedData,
        dentalAssistants: [] as typeof processedData,
        floating: [] as typeof processedData,
      };
      stationGroups.set(title, next);
      return next;
    };

    sortedData
      .filter(d => getTaskFilterKey(d.currentTask, d.currentSubTask) !== '尚未分配')
      .forEach(d => {
        const task = getTaskFilterKey(d.currentTask, d.currentSubTask);

        if (task === '流動') {
          const targetStation = normalizeWorkStation(d.currentSubTask);
          const group = ensureStationGroup(targetStation || '流動');
          if (targetStation) {
            group.floating.push(d);
          } else {
            group.primary.push(d);
          }
          return;
        }

        if (isDentalAssistantTask(task)) {
          ensureStationGroup('牙科').dentalAssistants.push(d);
          return;
        }

        if (task === '跟診') {
          const targetStation = normalizeWorkStation(d.currentSubTask);
          const group = ensureStationGroup(targetStation || '跟診');
          if (targetStation) {
            group.clinicAssistants.push(d);
          } else {
            group.primary.push(d);
          }
          return;
        }

        ensureStationGroup(task).primary.push(d);
      });

    const stationOrder = [
      ...WORK_ASSIGNMENT_EXPORT_ORDER.filter(station => stationGroups.has(station)),
      ...Array.from(stationGroups.keys()).filter(station => !WORK_ASSIGNMENT_EXPORT_ORDER.includes(station)).sort((a, b) => a.localeCompare(b, 'zh-Hant')),
    ];

    const taskRows = stationOrder.map((station, index) => {
      const group = stationGroups.get(station);
      const stationConfig = getWorkStationConfig(station);
      const primaryNames = new Set((group?.primary || []).map(person => person.name));
      const assistantNameWithFallbackTarget = (person: typeof processedData[number]) =>
        person.currentAssistTarget && !primaryNames.has(person.currentAssistTarget)
          ? `${personName(person)}（跟${person.currentAssistTarget}）`
          : personName(person);
      const assistantsFor = (items: typeof processedData, target: string) =>
        items.filter(person => person.currentAssistTarget === target);
      const unattachedAssistants = (items: typeof processedData) =>
        items.filter(person => !person.currentAssistTarget || !primaryNames.has(person.currentAssistTarget));
      const primaryChunks = (group?.primary || []).map(primary => {
        const clinic = assistantsFor(group?.clinicAssistants || [], primary.name);
        const dental = assistantsFor(group?.dentalAssistants || [], primary.name);
        return [
          personName(primary),
          clinic.length ? `跟診：${clinic.map(personName).join('、')}` : '',
          dental.length ? `牙助：${dental.map(personName).join('、')}` : '',
        ].filter(Boolean).join(' ');
      });
      const unattachedClinicAssistants = unattachedAssistants(group?.clinicAssistants || []);
      const unattachedDentalAssistants = unattachedAssistants(group?.dentalAssistants || []);
      const chunks = [
        stationConfig?.primaryLabel && group?.primary.length
          ? stationConfig?.primaryLabel
            ? `${stationConfig.primaryLabel}：${group.primary.map(personName).join('、')}`
            : group.primary.map(personName).join('、')
          : '',
        !stationConfig?.primaryLabel && primaryChunks.length
          ? primaryChunks.join('；')
          : '',
        unattachedClinicAssistants.length ? `跟診：${unattachedClinicAssistants.map(assistantNameWithFallbackTarget).join('、')}` : '',
        unattachedDentalAssistants.length ? `牙助：${unattachedDentalAssistants.map(assistantNameWithFallbackTarget).join('、')}` : '',
        group?.floating.length ? `流動：${group.floating.map(personName).join('、')}` : '',
      ].filter(Boolean);

      return [`${sectionNumber(index)}、${station}`, chunks.join('；')];
    });

    const unassignedNames = sortedData
      .filter(d => getTaskFilterKey(d.currentTask, d.currentSubTask) === '尚未分配' && d.currentLeadership === '無')
      .map(personName);

    const footerRows = [
      ['宣導事項', '※手圈識別：主內──紅色；社區民眾──藍色'],
      ['', '※請禮讓非主內之社區民眾優先看診及剪髮服務'],
    ];

    const escape = (c: string) => `"${String(c ?? '').replace(/"/g, '""')}"`;
    const toLine = (row: string[]) => row.map(escape).join(',');
    const lines = [
      [`喜信愛心醫療宣道義診團 ${selectedEvent.dateRange}`, selectedEvent.location],
      [leadershipLine || '隊職：尚未安排'],
      [],
      ['工作分配表', eventDateForSheet],
      ...taskRows,
      ...(leadershipOnlyLine ? [['隊職', leadershipOnlyLine]] : []),
      ...(unassignedNames.length > 0 ? [['尚未分配', unassignedNames.join('、')]] : []),
      [],
      ...footerRows,
    ];
    const csvContent = '\uFEFF' + lines.map(toLine).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const safeName = `${selectedEvent.dateRange}_${selectedEvent.location}_工作分配表`.replace(/[\\/:*?"<>|]+/g, '_');
    link.href = url;
    link.download = `${safeName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast('success', '匯出成功', '工作分配表 CSV 已下載');
  };
  // Update date range when date inputs change
  const updateDateRange = (month: string, startDay: string, endDay: string) => {
    if (!month || !startDay) return;

    const monthNum = month.padStart(2, '0');
    const startDayNum = startDay.padStart(2, '0');

    let dateRangeStr = '';
    if (endDay && endDay !== startDay) {
      const endDayNum = endDay.padStart(2, '0');
      dateRangeStr = `${monthNum}月${startDayNum}-${endDayNum}日`;
    } else {
      dateRangeStr = `${monthNum}月${startDayNum}日`;
    }

    // 預設：報名截止日與線上取消截止日＝出隊前一個月（管理員可再修改）
    const start = new Date(dashboardYear, parseInt(month, 10) - 1, parseInt(startDay, 10));
    const cutoff = getDefaultCutoffFromStart(start);
    const cutoffIso = toIsoDate(cutoff);
    const cutoffLabel = toMonthDayLabel(cutoff);
    const cutoffMonth = String(cutoff.getMonth() + 1);
    const cutoffDay = String(cutoff.getDate());

    setNewEvent(prev => ({
      ...prev,
      dateRange: dateRangeStr,
      startDate: toIsoDate(start),
      ...(cutoffTouched.deadline ? {} : { deadline: cutoffLabel, deadlineDate: cutoffIso }),
      ...(cutoffTouched.cancel ? {} : { cancelDeadline: cutoffLabel, cancelDeadlineDate: cutoffIso }),
    }));
    setDateInput(prev => ({
      ...prev,
      ...(cutoffTouched.deadline ? {} : { deadlineMonth: cutoffMonth, deadlineDay: cutoffDay }),
      ...(cutoffTouched.cancel ? {} : { cancelMonth: cutoffMonth, cancelDay: cutoffDay }),
    }));
  };

  // Update deadline when deadline inputs change
  const updateDeadline = (month: string, day: string) => {
    if (!month || !day) return;

    const monthNum = month.padStart(2, '0');
    const dayNum = day.padStart(2, '0');
    const deadlineStr = `${monthNum}月${dayNum}日`;
    const deadlineDateStr = `${dashboardYear}-${monthNum}-${dayNum}`;

    setCutoffTouched(prev => ({ ...prev, deadline: true }));
    setNewEvent(prev => ({
      ...prev,
      deadline: deadlineStr,
      deadlineDate: deadlineDateStr
    }));
  };

  // 線上取消截止日（手動調整）
  const updateCancelDeadline = (month: string, day: string) => {
    if (!month || !day) return;
    const monthNum = month.padStart(2, '0');
    const dayNum = day.padStart(2, '0');
    setCutoffTouched(prev => ({ ...prev, cancel: true }));
    setNewEvent(prev => ({
      ...prev,
      cancelDeadline: `${monthNum}月${dayNum}日`,
      cancelDeadlineDate: `${dashboardYear}-${monthNum}-${dayNum}`
    }));
  };

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEvent.dateRange || !newEvent.location || !newEvent.deadline) {
      showToast('error', '資料不完整', '請填寫所有欄位');
      return;
    }

    // Ensure new event gets the current system year
    const capacityNum = newEvent.capacity ? parseInt(newEvent.capacity) : undefined;
    onUpdateEvents([...events, {
      id: `evt_${Date.now()}`,
      year: dashboardYear,
      dateRange: newEvent.dateRange,
      location: newEvent.location,
      deadline: newEvent.deadline,
      deadlineDate: newEvent.deadlineDate,
      ...(newEvent.startDate ? { startDate: newEvent.startDate } : {}),
      ...(newEvent.cancelDeadlineDate ? { cancelDeadline: newEvent.cancelDeadline, cancelDeadlineDate: newEvent.cancelDeadlineDate } : {}),
      ...(capacityNum && capacityNum > 0 ? { capacity: capacityNum } : {}),
      type: newEvent.type
    }]);

    // Reset form
    setNewEvent({ dateRange: '', location: '', deadline: '', deadlineDate: '', cancelDeadline: '', cancelDeadlineDate: '', startDate: '', capacity: '', type: 'mission' });
    setDateInput({ month: '', startDay: '', endDay: '', deadlineMonth: '', deadlineDay: '', cancelMonth: '', cancelDay: '' });
    setCutoffTouched({ deadline: false, cancel: false });
    showToast('success', '新增成功', '梯隊已建立');
  };

  const handleDeleteEvent = (id: string) => {
    if (window.confirm('確定要刪除此梯隊嗎？')) {
      onUpdateEvents(events.filter(ev => ev.id !== id));
      if (selectedEventId === id) setSelectedEventId('');
    }
  };

  const startEditingEvent = (ev: EventOption) => {
    setEditingEventId(ev.id);
    setEditForm(ev);
  };

  const cancelEditingEvent = () => {
    setEditingEventId(null);
    setEditForm(null);
  };

  const saveEditingEvent = () => {
    if (editForm) {
      onEditEvent(editForm);
      cancelEditingEvent();
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1920px] mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 tracking-tight">醫宣報名管理後台</h1>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {availableYears.map(year => (
                <button
                  key={year}
                  onClick={() => setDashboardYear(year)}
                  className={`px-3 py-1.5 rounded-md text-sm font-bold transition-colors ${
                    dashboardYear === year
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: Dropdown Menu */}
        <div className="lg:hidden w-full">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as DashboardTab)}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-bold focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all"
          >
            <option value="registrations">📋 名單管理</option>
            <option value="events">📅 梯隊設定</option>
            <option value="inventory">📦 物資管理</option>
            <option value="outlook-records">📝 展望會紀錄</option>
            <option value="settings">⚙️ 系統設定</option>
          </select>
        </div>

        {/* Desktop: Tab Buttons */}
        <div className="hidden lg:flex space-x-1 bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
          {['registrations', 'events', 'inventory', 'outlook-records', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as DashboardTab)}
              className={`flex-1 lg:flex-none px-4 md:px-6 py-2.5 rounded-lg text-sm md:text-base font-bold flex justify-center items-center whitespace-nowrap transition-all duration-200 ${activeTab === tab ? 'bg-indigo-100 text-indigo-800 shadow-inner' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {tab === 'registrations' && <List size={18} className="mr-2" />}
              {tab === 'events' && <Calendar size={18} className="mr-2" />}
              {tab === 'inventory' && <Package size={18} className="mr-2" />}
              {tab === 'outlook-records' && <ClipboardList size={18} className="mr-2" />}
              {tab === 'settings' && <Settings size={18} className="mr-2" />}
              {tab === 'registrations'
                ? '名單管理'
                : tab === 'events'
                  ? '梯隊設定'
                  : tab === 'inventory'
                    ? '物資管理'
                    : tab === 'outlook-records'
                      ? '展望會紀錄'
                      : '系統設定'}
            </button>
          ))}
        </div>
      </div>

      <div className="animate-fade-in-up">
        {activeTab === 'registrations' && (
          <>
            {isEventDataLoading ? (
              <div className="p-8 bg-white rounded-2xl border-2 border-dashed border-indigo-100 text-center text-gray-500">
                <RotateCw size={48} className="mx-auto mb-3 text-indigo-400 animate-spin" />
                <p className="text-xl font-bold mb-2">梯隊資料載入中...</p>
                <p>正在同步 {dashboardYear} 年度梯隊資料，請稍候。</p>
              </div>
            ) : dashboardEvents.length === 0 ? (
              <div className="p-8 bg-white rounded-2xl border-2 border-dashed text-center text-gray-500">
                <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-xl font-bold mb-2">目前 {dashboardYear} 年度尚未建立任何梯隊</p>
                <p>請前往「梯隊設定」頁籤新增梯隊。</p>
              </div>
            ) : (
              <>
                {/* 1. Event Selector - Dropdown Style (Optimized for Mobile) */}
                <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-200 mb-6">
                  <div className="flex flex-col md:flex-row gap-4 items-center">
                    <div className="w-full md:w-1/2 lg:w-1/3 relative group z-20">
                      <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">選擇梯隊</label>
                      <div className="relative">
                        <select
                          data-layout-audit="event-selector"
                          className="w-full pl-4 pr-10 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-lg font-bold text-gray-800 appearance-none focus:outline-none focus:border-indigo-500 focus:bg-white transition-all cursor-pointer shadow-sm"
                          value={selectedEventId}
                          onChange={(e) => setSelectedEventId(e.target.value)}
                        >
                          {dashboardEvents.map(evt => (
                            <option key={evt.id} value={evt.id}>
                              {evt.dateRange} - {evt.location}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-indigo-500 transition-colors" size={20} />
                      </div>
                    </div>

                    {/* Quick Summary Stats next to selector on desktop */}
                    {selectedEventId && (() => {
                      const selectedEvent = dashboardEvents.find(e => e.id === selectedEventId);
                      const approvedCount = approvedCountByEvent[selectedEventId] || 0;
                      const hasCapacity = selectedEvent?.capacity && selectedEvent.capacity > 0;
                      const isFull = selectedEvent?.registrationClosed;

                      return (
                        <div className="w-full md:w-auto flex-1 grid grid-cols-1 sm:grid-cols-2 md:flex md:items-center md:justify-end gap-3">
                          <div className="bg-indigo-50 text-indigo-900 px-5 py-3 rounded-xl font-medium flex items-center w-full md:w-auto justify-between md:justify-start min-w-0">
                            <span className="flex items-center mr-4 shrink-0 whitespace-nowrap [word-break:keep-all]"><User size={20} className="mr-2 shrink-0" /> 總報名</span>
                            <span className="text-2xl font-bold">{processedData.length} 人</span>
                          </div>
                          <div className={`px-5 py-3 rounded-xl font-medium flex items-center justify-between md:justify-start min-w-0 ${isFull ? 'bg-orange-50 text-orange-900' : 'bg-green-50 text-green-900'}`}>
                            <span className="flex items-center mr-4 shrink-0 whitespace-nowrap [word-break:keep-all]"><Users size={20} className="mr-2 shrink-0" /> 已核准</span>
                            <span className="text-2xl font-bold whitespace-nowrap tabular-nums">
                              {approvedCount}{hasCapacity ? ` / ${selectedEvent!.capacity}` : ''}
                            </span>
                            {isFull && (
                              <span className="ml-2 text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full font-bold whitespace-nowrap">已關閉</span>
                            )}
                          </div>
                          {isFirstTimeDataReady && (
                            <div
                              className="bg-violet-50 text-violet-900 px-5 py-3 rounded-xl font-medium flex items-center justify-between md:justify-start min-w-0 sm:col-span-2 md:col-span-1"
                              title={FIRST_TIME_PARTICIPANT_TOOLTIP}
                            >
                              <span className="flex items-center mr-4 shrink-0 whitespace-nowrap [word-break:keep-all]"><Sparkles size={20} className="mr-2 shrink-0" /> 第一次參與</span>
                              <span className="text-2xl font-bold whitespace-nowrap tabular-nums">{firstTimeCount} 人</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {selectedEventId && (
                  <>
                    {!assignmentMode && (
                      <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-200 mb-6 animate-fade-in">
                        <div className="flex items-center text-gray-700 font-bold whitespace-nowrap mb-4">
                          <Stethoscope className="mr-2 text-indigo-600" size={20} />
                          <span className="mr-2 text-lg">人力監控儀表板</span>
                          <span className="text-xs text-gray-500 font-normal hidden md:inline">(紅燈表示無人報名)</span>
                          <span className="ml-auto flex items-center gap-1 bg-gray-100 text-gray-600 font-normal rounded-lg px-2.5 py-1 text-xs whitespace-nowrap">
                            總計 <b className="text-sm text-gray-800 font-bold">{eventStats.total}</b>
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                          {[
                            { label: '西醫', count: eventStats.westMed },
                            { label: '中醫', count: eventStats.tcm },
                            { label: '牙醫', count: eventStats.dentist },
                            { label: '護理', count: eventStats.nurse },
                            { label: '藥師', count: eventStats.pharmacist },
                            { label: '物理治療師', count: eventStats.physio },
                          ].map(stat => (
                            <div
                              key={stat.label}
                              className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl border-2 font-bold transition-all
                                ${stat.count === 0
                                  ? 'bg-red-50 border-red-100 text-red-700 shadow-sm'
                                  : 'bg-green-50 border-green-100 text-green-800'
                                }`}
                            >
                              <span className="text-sm md:text-base mb-1 md:mb-0 whitespace-nowrap">{stat.count === 0 ? `缺${stat.label}` : stat.label}</span>
                              <div className="flex items-center">
                                {stat.count === 0 && <AlertTriangle size={16} className="mr-1 animate-pulse" />}
                                <span className="text-xl md:text-xl">{stat.count}</span>
                              </div>
                            </div>
                          ))}
                          <div
                            className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl border-2 font-bold transition-all
                              ${eventStats.unassignedTask > 0
                                ? 'bg-amber-50 border-amber-100 text-amber-800'
                                : 'bg-gray-50 border-gray-200 text-gray-500'
                              }`}
                          >
                            <span className="text-sm md:text-base mb-1 md:mb-0 whitespace-nowrap">尚未分配</span>
                            <div className="flex items-center">
                              {eventStats.unassignedTask > 0 && <AlertTriangle size={16} className="mr-1" />}
                              <span className="text-xl md:text-xl">{eventStats.unassignedTask}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 3. Controls Bar */}
                    <div data-layout-audit="controls-bar" className="flex flex-col min-[1800px]:flex-row justify-between items-stretch min-[1800px]:items-center mb-6 gap-4 bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-200 overflow-visible">
                      <div data-layout-audit="controls-filters" className="grid w-full min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(16rem,1.15fr)_minmax(12rem,0.9fr)_minmax(12rem,0.9fr)_minmax(12rem,0.9fr)] min-[1800px]:w-auto min-[1800px]:grid-cols-[minmax(16rem,18rem)_minmax(12rem,14rem)_minmax(12rem,14rem)_minmax(12rem,14rem)]">
                        <div className="relative w-full min-w-0 group">
                          <input
                            type="text"
                            placeholder="搜尋姓名、教會..."
                            className="w-full pl-10 pr-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <Search className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                        </div>
                        <div className="relative w-full min-w-0 group">
                          <select
                            className="w-full pl-10 pr-10 py-2.5 text-base border-2 border-gray-200 rounded-xl appearance-none bg-white cursor-pointer focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all font-medium text-gray-700"
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                          >
                            <option value={ROLE_FILTER_ALL}>所有職類 ({processedData.length})</option>
                            {roleFilterOptions.map(role => (
                              <option key={role} value={role}>
                                {role} ({roleFilterCounts[role] || 0})
                              </option>
                            ))}
                          </select>
                          <User className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                          <ChevronDown className="absolute right-3 top-3.5 text-gray-400" size={16} />
                        </div>
                        <div className="relative w-full min-w-0 group">
                          <select
                            className="w-full pl-10 pr-10 py-2.5 text-base border-2 border-gray-200 rounded-xl appearance-none bg-white cursor-pointer focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all font-medium text-gray-700"
                            value={filterTask}
                            onChange={(e) => setFilterTask(e.target.value)}
                          >
                            <option value={TASK_FILTER_ALL}>所有工作 ({processedData.length})</option>
                            {taskFilterOptions.map(task => (
                              <option key={task} value={task}>
                                {task} ({taskFilterCounts[task] || 0})
                              </option>
                            ))}
                          </select>
                          <List className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                          <ChevronDown className="absolute right-3 top-3.5 text-gray-400" size={16} />
                        </div>
                        <div className="relative w-full min-w-0 group">
                          <select
                            aria-label="排序方式"
                            title="排序方式"
                            className="w-full pl-10 pr-10 py-2.5 text-base border-2 border-gray-200 rounded-xl appearance-none bg-white cursor-pointer focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all font-medium text-gray-700"
                            value={registrantSort}
                            onChange={(e) => {
                              if (isRegistrantSortMode(e.target.value)) setRegistrantSort(e.target.value);
                            }}
                          >
                            {REGISTRANT_SORT_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                排序：{option.label}
                              </option>
                            ))}
                          </select>
                          <ArrowUpDown className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                          <ChevronDown className="absolute right-3 top-3.5 text-gray-400" size={16} />
                        </div>
                      </div>
                      <div data-layout-audit="controls-actions" className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 min-[1800px]:flex min-[1800px]:w-auto min-[1800px]:flex-wrap min-[1800px]:items-center min-[1800px]:justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setAssignmentMode(prev => !prev);
                            setSelectedForBatch(new Set());
                          }}
                          className={`flex w-full min-w-0 items-center justify-center px-4 py-2.5 rounded-xl text-base font-bold transition-all active:scale-95 shadow-sm min-[1800px]:w-auto min-[1800px]:whitespace-nowrap ${
                            assignmentMode
                              ? 'bg-indigo-700 text-white hover:bg-indigo-800'
                              : 'bg-indigo-50 text-indigo-800 border-2 border-indigo-100 hover:bg-indigo-100'
                          }`}
                        >
                          <List size={18} className="mr-2 shrink-0" />
                          <span className="truncate">{assignmentMode ? '結束排工作' : '排工作模式'}</span>
                        </button>
                        {selectedForBatch.size > 0 && !assignmentMode && (
                          <div className="flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-lg border-2 border-indigo-300 animate-fade-in sm:col-span-2 lg:col-span-3 xl:col-span-5 min-[1800px]:col-span-1">
                            <span className="text-sm font-bold text-indigo-700">已選 {selectedForBatch.size} 人</span>
                            <button
                              onClick={handleBatchApprove}
                              className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-bold transition-all active:scale-95 shadow-sm flex items-center"
                            >
                              <CheckCircle size={16} className="mr-1" />
                              批次通過
                            </button>
                            <button
                              onClick={() => setSelectedForBatch(new Set())}
                              className="px-3 py-1.5 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-sm font-bold transition-all active:scale-95"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        <button
                          onClick={handleExportAssignments}
                          className="flex w-full min-w-0 items-center justify-center px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-base font-bold transition-all active:scale-95 shadow-sm hover:shadow-md btn-press min-[1800px]:w-auto min-[1800px]:whitespace-nowrap"
                        >
                          <FileSpreadsheet size={18} className="mr-2 shrink-0" />
                          <span className="truncate">匯出工作分配表</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleExportRegistrationRoster}
                          className="flex w-full min-w-0 items-center justify-center px-5 py-2.5 bg-white text-green-700 border-2 border-green-200 rounded-xl hover:bg-green-50 hover:border-green-300 text-base font-bold transition-all active:scale-95 shadow-sm hover:shadow-md btn-press min-[1800px]:w-auto min-[1800px]:whitespace-nowrap"
                        >
                          <FileSpreadsheet size={18} className="mr-2 shrink-0" />
                          <span className="truncate">匯出報名名冊</span>
                        </button>
                        <button
                          onClick={() => setShowAddMemberModal(true)}
                          disabled={!selectedEventId}
                          className="flex w-full min-w-0 items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all min-[1800px]:w-auto"
                        >
                          <Plus size={18} className="mr-1.5 shrink-0" />
                          <span className="truncate">新增成員</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCustomTaskManager(prev => !prev)}
                          disabled={!selectedEventId}
                          className={`flex items-center px-4 py-2 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            showCustomTaskManager
                              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                              : 'bg-white text-emerald-700 border-2 border-emerald-200 hover:bg-emerald-50'
                          } w-full min-w-0 justify-center min-[1800px]:w-auto`}
                          title="管理自訂工作"
                        >
                          <Settings size={18} className="mr-1.5 shrink-0" />
                          <span className="truncate">自訂工作</span>
                        </button>
                      </div>
                    </div>

                    {assignmentMode && (
                      <div className={`mb-6 rounded-2xl border shadow-sm ${
                        selectedForBatch.size === 0
                          ? 'border-gray-200 bg-white px-4 py-3 md:px-5'
                          : 'border-indigo-100 bg-indigo-50/70 px-4 py-4 md:px-5'
                      }`}>
                        {selectedForBatch.size === 0 ? (
                          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                                <Users size={18} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-extrabold text-gray-900">批量分工</div>
                                <div className="mt-0.5 flex items-center gap-2 text-sm font-bold text-gray-500">
                                  <span>尚未勾選人員</span>
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">0 人</span>
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleSelectAllFiltered(true)}
                              className="inline-flex w-full items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-bold text-indigo-700 hover:bg-indigo-100 md:w-auto"
                            >
                              選取目前篩選
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
                            <div className="xl:w-52">
                              <div className="text-xs font-bold text-indigo-500">已勾選</div>
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-2xl font-extrabold text-indigo-900">{selectedForBatch.size}</span>
                                <span className="text-sm font-bold text-indigo-700">人</span>
                                <button
                                  type="button"
                                  onClick={() => handleSelectAllFiltered(true)}
                                  className="ml-auto rounded-lg bg-white px-3 py-2 text-sm font-bold text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                                >
                                  選取目前篩選
                                </button>
                              </div>
                            </div>

                            <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-4">
                              <label className="block">
                                <span className="text-xs font-bold text-indigo-700">批量職務</span>
                                <select
                                  value={batchAssignmentLeadership}
                                  onChange={(e) => setBatchAssignmentLeadership(e.target.value)}
                                  className="mt-1 w-full rounded-lg border-2 border-indigo-100 bg-white px-3 py-2.5 text-sm font-bold text-gray-800 focus:border-indigo-500 focus:outline-none"
                                >
                                  <option value="">職務不變</option>
                                  {LEADERSHIP_ROLES.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                  ))}
                                </select>
                              </label>

                              <label className="block">
                                <span className="text-xs font-bold text-indigo-700">批量工作</span>
                                <select
                                  value={batchAssignmentTask}
                                  onChange={(e) => setBatchAssignmentTask(e.target.value)}
                                  className="mt-1 w-full rounded-lg border-2 border-indigo-100 bg-white px-3 py-2.5 text-sm font-bold text-gray-800 focus:border-indigo-500 focus:outline-none"
                                >
                                  <option value="">工作不變</option>
                                  {taskOptions.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                  ))}
                                </select>
                              </label>

                              {batchAssignmentTask === '流動' && (
                                <label className="block">
                                  <span className="text-xs font-bold text-indigo-700">流動站點</span>
                                  <select
                                    value={batchAssignmentSubDetail}
                                    onChange={(e) => setBatchAssignmentSubDetail(e.target.value)}
                                    className="mt-1 w-full rounded-lg border-2 border-indigo-100 bg-white px-3 py-2.5 text-sm font-bold text-gray-800 focus:border-indigo-500 focus:outline-none"
                                  >
                                    <option value="">未指定站點</option>
                                    {stationTargetOptions.map(station => (
                                      <option key={station} value={station}>{station}</option>
                                    ))}
                                  </select>
                                </label>
                              )}

                              {batchAssignmentTask === '跟診' && (
                                <label className="block">
                                  <span className="text-xs font-bold text-indigo-700">跟診站點</span>
                                  <select
                                    value={batchAssignmentSubDetail}
                                    onChange={(e) => setBatchAssignmentSubDetail(e.target.value)}
                                    className="mt-1 w-full rounded-lg border-2 border-indigo-100 bg-white px-3 py-2.5 text-sm font-bold text-gray-800 focus:border-indigo-500 focus:outline-none"
                                  >
                                    <option value="">未指定站點</option>
                                    {stationTargetOptions.map(station => (
                                      <option key={station} value={station}>{station}</option>
                                    ))}
                                  </select>
                                </label>
                              )}

                              {taskSupportsAssistTarget(batchAssignmentTask) && (
                                <label className="block">
                                  <span className="text-xs font-bold text-indigo-700">
                                    {isDentalAssistantTask(batchAssignmentTask) ? '牙助對象' : '跟診對象'}
                                  </span>
                                  <select
                                    value={batchAssignmentAssistTarget}
                                    onChange={(e) => setBatchAssignmentAssistTarget(e.target.value)}
                                    className="mt-1 w-full rounded-lg border-2 border-indigo-100 bg-white px-3 py-2.5 text-sm font-bold text-gray-800 focus:border-indigo-500 focus:outline-none"
                                  >
                                    <option value="">未指定對象</option>
                                    {getAssistTargetOptions(isDentalAssistantTask(batchAssignmentTask) ? '牙科' : batchAssignmentSubDetail).map(name => (
                                      <option key={name} value={name}>{name}</option>
                                    ))}
                                  </select>
                                </label>
                              )}

                              <label className="block md:col-span-2">
                                <span className="text-xs font-bold text-indigo-700">批量分工備註</span>
                                <input
                                  type="text"
                                  value={batchAssignmentNote}
                                  onChange={(e) => setBatchAssignmentNote(e.target.value)}
                                  placeholder="選填，空白則不變"
                                  className="mt-1 w-full rounded-lg border-2 border-indigo-100 bg-white px-3 py-2.5 text-sm font-bold text-gray-800 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none"
                                />
                              </label>
                            </div>

                            <div className="flex gap-2 xl:w-auto">
                              <button
                                type="button"
                                onClick={handleBatchAssignmentApply}
                                className="flex-1 rounded-lg bg-indigo-700 px-4 py-3 text-sm font-extrabold text-white shadow-sm hover:bg-indigo-800 xl:flex-none"
                              >
                                套用分工
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedForBatch(new Set())}
                                className="rounded-lg bg-white px-4 py-3 text-sm font-bold text-gray-600 border border-gray-200 hover:bg-gray-50"
                              >
                                清除
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedEvent && (
                      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
                          <div className="text-xs font-bold text-indigo-500">已分配工作</div>
                          <div className="mt-1 text-2xl font-extrabold text-indigo-900">
                            {assignmentStats.taskAssigned}
                            <span className="ml-1 text-sm font-bold text-indigo-500">/ {assignmentStats.total}</span>
                          </div>
                        </div>
                        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                          <div className="text-xs font-bold text-amber-600">尚未分配</div>
                          <div className="mt-1 text-2xl font-extrabold text-amber-900">{assignmentStats.pendingTask}</div>
                        </div>
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                          <div className="text-xs font-bold text-emerald-600">已設定隊職</div>
                          <div className="mt-1 text-2xl font-extrabold text-emerald-900">{assignmentStats.leadershipAssigned}</div>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                          <div className="text-xs font-bold text-gray-500">未遴選</div>
                          <div className="mt-1 text-2xl font-extrabold text-gray-800">{assignmentStats.pendingApproval}</div>
                        </div>
                      </div>
                    )}

                    {showCustomTaskManager && selectedEvent && (
                      <div className="mb-6 bg-white p-4 md:p-5 rounded-xl shadow-sm border border-emerald-200">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 flex items-center">
                              <Settings size={20} className="mr-2 text-emerald-600" />
                              自訂工作管理
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              梯隊：{selectedEvent.dateRange} - {selectedEvent.location}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCustomTaskManager(false);
                              setEditingCustomTask(null);
                            }}
                            className="self-start md:self-auto p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                            title="關閉"
                          >
                            <X size={18} />
                          </button>
                        </div>

                        {customTaskRolesForManagement.length === 0 ? (
                          <div className="px-4 py-3 rounded-lg bg-gray-50 border border-dashed border-gray-300 text-sm text-gray-500">
                            目前沒有自訂工作。可在分工下拉選「其他」新增。
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {customTaskRolesForManagement.map(role => {
                              const isEditingRole = editingCustomTask?.original === role;
                              const usageCount = customTaskUsageCounts[role] || 0;

                              return (
                                <div key={role} className="flex flex-col md:flex-row md:items-center gap-3 px-3 py-3 rounded-lg border border-gray-200 bg-gray-50">
                                  {isEditingRole ? (
                                    <div className="flex flex-col md:flex-row gap-2 flex-1 min-w-0">
                                      <input
                                        type="text"
                                        value={editingCustomTask.value}
                                        onChange={(e) => setEditingCustomTask({ original: role, value: e.target.value })}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveCustomTaskRole(role);
                                          if (e.key === 'Escape') setEditingCustomTask(null);
                                        }}
                                        className="min-w-0 flex-1 px-3 py-2 rounded-lg border-2 border-emerald-200 bg-white text-emerald-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                                        autoFocus
                                      />
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleSaveCustomTaskRole(role)}
                                          className="inline-flex items-center px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-bold transition-colors"
                                          title="儲存"
                                        >
                                          <Save size={16} className="mr-1.5" /> 儲存
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setEditingCustomTask(null)}
                                          className="inline-flex items-center px-3 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 font-bold transition-colors"
                                          title="取消"
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-bold text-gray-900 truncate">{role}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">目前指派 {usageCount} 人</div>
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => setEditingCustomTask({ original: role, value: role })}
                                          className="inline-flex items-center px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 font-bold transition-colors"
                                          title="修改"
                                        >
                                          <Pencil size={16} className="mr-1.5" /> 修改
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteCustomTaskRole(role)}
                                          className="inline-flex items-center px-3 py-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 font-bold transition-colors"
                                          title="刪除"
                                        >
                                          <Trash2 size={16} className="mr-1.5" /> 刪除
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 4. Data Display Area */}
                    {assignmentMode ? (
                      <>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          {filteredData.map(r => (
                            <div
                              key={r.id}
                              className={`rounded-2xl border bg-white p-4 shadow-sm ${
                                selectedForBatch.has(r.id) ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-gray-200'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={selectedForBatch.has(r.id)}
                                  onChange={(e) => handleBatchSelection(r.id, e.target.checked)}
                                  className="mt-1 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className="truncate text-lg font-extrabold text-gray-900">{r.name}</div>
                                    {renderFirstTimeBadge(r)}
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                      getTaskFilterKey(r.currentTask, r.currentSubTask) === '尚未分配'
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-indigo-100 text-indigo-700'
                                    }`}>
                                      {formatTaskDisplay(r.currentTask, r.currentSubTask)}
                                    </span>
                                  </div>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {(r.roles && r.roles.length > 0 ? r.roles : [r.role]).map((role, idx) => (
                                      <span key={idx} className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-600">
                                        {role.split('~')[0].split('(')[0]}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <div className="mt-4 grid grid-cols-2 gap-3">
                                <label className="block">
                                  <span className="ml-1 text-xs font-bold text-indigo-800">職務</span>
                                  <select
                                    className={`mt-1 w-full rounded-lg border px-2 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
                                      r.currentLeadership !== '無'
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white text-gray-700 border-gray-300'
                                    }`}
                                    value={r.currentLeadership}
                                    onChange={(e) => handleAssignmentChange(r.id, 'leadershipRole', e.target.value)}
                                  >
                                    {LEADERSHIP_ROLES.map(role => (
                                      <option key={role} value={role} className="bg-white text-gray-900">
                                        {role}
                                      </option>
                                    ))}
                                  </select>
                                </label>

                                <label className="block">
                                  <span className="ml-1 text-xs font-bold text-indigo-800">工作</span>
                                  <select
                                    className={`mt-1 w-full rounded-lg border px-2 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
                                      r.currentTask !== '尚未分配'
                                        ? 'bg-white text-indigo-800 border-indigo-300 ring-1 ring-indigo-100'
                                        : 'bg-white text-gray-700 border-gray-300'
                                    }`}
                                    value={r.currentTask}
                                    onChange={(e) => handleAssignmentChange(r.id, 'taskRole', e.target.value)}
                                  >
                                    {taskOptions.map(role => (
                                      <option key={role} value={role} className="bg-white text-gray-900">
                                        {role}
                                      </option>
                                    ))}
                                  </select>
                                </label>

                                {r.currentTask === '跟診' && (
                                  <div className="col-span-2">
                                    <select
                                      className="w-full px-2 py-2 rounded-lg text-sm font-bold border bg-yellow-50 border-yellow-200 text-yellow-800 focus:outline-none focus:ring-2 focus:ring-yellow-200 text-center cursor-pointer"
                                      value={r.currentSubTask}
                                      onChange={(e) => handleAssignmentChange(r.id, 'taskSubDetail', e.target.value)}
                                    >
                                      <option value="">選擇跟診站點...</option>
                                      {stationTargetOptions.map(station => (
                                        <option key={station} value={station}>{station}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {r.currentTask === '流動' && (
                                  <div className="col-span-2">
                                    <select
                                      className="w-full px-2 py-2 rounded-lg text-sm font-bold border bg-blue-50 border-blue-200 text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-200 text-center cursor-pointer"
                                      value={r.currentSubTask}
                                      onChange={(e) => handleAssignmentChange(r.id, 'taskSubDetail', e.target.value)}
                                    >
                                      <option value="">選擇流動站點...</option>
                                      {stationTargetOptions.map(station => (
                                        <option key={station} value={station}>{station}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {taskSupportsAssistTarget(r.currentTask) && (
                                  <div className="col-span-2">
                                    <select
                                      className="w-full px-2 py-2 rounded-lg text-sm font-bold border bg-violet-50 border-violet-200 text-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-200 text-center cursor-pointer"
                                      value={r.currentAssistTarget}
                                      onChange={(e) => handleAssignmentChange(r.id, 'assistTarget', e.target.value)}
                                    >
                                      <option value="">{isDentalAssistantTask(r.currentTask) ? '選擇牙助對象...' : '選擇跟診對象...'}</option>
                                      {getAssistTargetSelectOptions(r).map(name => (
                                        <option key={name} value={name}>{name}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {r.currentTask === '其他' && (
                                  <div className="col-span-2">
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        className="min-w-0 flex-1 px-2 py-2 rounded-lg text-sm font-bold border bg-emerald-50 border-emerald-200 text-emerald-800 placeholder-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 text-center"
                                        value={customTaskInput[r.id] ?? r.currentSubTask}
                                        placeholder="輸入自訂職位..."
                                        onChange={(e) => setCustomTaskInput(prev => ({ ...prev, [r.id]: e.target.value }))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleAddCustomTask(r.id);
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleAddCustomTask(r.id)}
                                        disabled={!(customTaskInput[r.id] ?? r.currentSubTask).trim()}
                                        className="shrink-0 p-2 rounded-lg bg-emerald-600 text-white disabled:bg-emerald-200"
                                        title="加入自訂職位"
                                      >
                                        <Save size={16} />
                                      </button>
                                    </div>
                                  </div>
                                )}

                                <label className="col-span-2 block">
                                  <span className="ml-1 text-xs font-bold text-indigo-800">分工備註</span>
                                  <input
                                    type="text"
                                    value={r.assignments?.[selectedEventId]?.note || ''}
                                    onChange={(e) => handleAssignmentChange(r.id, 'note', e.target.value)}
                                    placeholder="例如：上午協助藥局流動"
                                    className="mt-1 w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:border-indigo-400 focus:outline-none"
                                  />
                                </label>
                              </div>
                            </div>
                          ))}
                          {filteredData.length === 0 && (
                            <div className="p-8 text-center text-gray-400 bg-white rounded-2xl border border-dashed">
                              無資料
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* A. Desktop View: Table (wide workbench) */}
                        <div data-layout-audit="desktop-roster" className="hidden xl:block bg-white rounded-2xl shadow-lg border border-gray-200">
                          <div className="overflow-visible">
                            <table className="w-full table-fixed text-left border-collapse">
                              <colgroup>
                                <col className="w-[52px]" />
                                <col className="w-[124px]" />
                                <col className="w-[250px]" />
                                <col className="w-[128px]" />
                                <col className="w-[250px]" />
                                <col className="w-[130px]" />
                                <col className="w-[190px]" />
                                <col className="w-[76px]" />
                              </colgroup>
                          <thead>
                            <tr className="bg-gray-100 border-b border-gray-200 text-gray-700 text-sm tracking-wide">
                              <th className={`${TABLE_HEADER_CELL} text-center`}>
                                <input
                                  type="checkbox"
                                  checked={selectedForBatch.size > 0 && selectedForBatch.size === filteredData.length}
                                  onChange={(e) => handleSelectAllFiltered(e.target.checked)}
                                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                              </th>
                              <th className={`${TABLE_HEADER_CELL} text-left text-gray-500`}>
                                遴選狀態
                              </th>
                              <th className={TABLE_HEADER_CELL}>人員 / 資格</th>
                              <th className={TABLE_HEADER_CELL}>聯繫</th>
                              <th className={TABLE_HEADER_CELL}>醫宣角色 / 報備</th>
                              <th className={`${TABLE_HEADER_CELL} bg-indigo-50/80 text-indigo-900`}>職務</th>
                              <th className={`${TABLE_HEADER_CELL} bg-indigo-50/80 text-indigo-900`}>任務分配</th>
                              <th className={`${TABLE_HEADER_CELL} text-center`}>操作</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredData.map((r) => (
                              <tr
                                key={r.id}
                                className={`group transition-all duration-150 hover:shadow-md relative z-0 hover:z-10 
                                ${!r.isApproved ? 'bg-gray-50 opacity-90' : 'hover:bg-gray-50'}
                              `}
                              >
                                {/* Batch Selection Checkbox */}
                                <td className={`${TABLE_BODY_CELL} text-center`}>
                                  <input
                                    type="checkbox"
                                    checked={selectedForBatch.has(r.id)}
                                    onChange={(e) => handleBatchSelection(r.id, e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                  />
                                </td>

                                {/* Approval Status - Redesigned with clear text button */}
                                <td className={TABLE_BODY_CELL}>
                                  <button
                                    onClick={() => handleApprovalToggle(r.id, r.isApproved)}
                                    className={`inline-flex min-w-[92px] items-center justify-center px-3 py-2 rounded-lg text-sm font-bold whitespace-nowrap [word-break:keep-all] transition-all active:scale-95 ${
                                      r.isApproved
                                        ? 'bg-green-100 text-green-700 border-2 border-green-300 hover:bg-green-200'
                                        : 'bg-gray-100 text-gray-600 border-2 border-gray-300 hover:bg-yellow-50 hover:border-yellow-400 hover:text-yellow-700'
                                    }`}
                                    title={r.isApproved ? "已遴選通過並上架，點擊可隱藏" : "待審核，點擊遴選通過並寄信"}
                                  >
                                    {r.isApproved ? '✓ 已通過' : '待審核'}
                                  </button>
                                </td>

                                {/* Name & Church */}
                                <td className={TABLE_BODY_CELL}>
                                  <div className={!r.isApproved ? 'text-gray-500' : ''}>
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                      <span className="text-lg font-bold leading-6 text-gray-900 whitespace-nowrap [word-break:keep-all]">{r.name}</span>
                                      {r.source === 'manual' && (
                                        <span className="align-middle inline-block bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">手動</span>
                                      )}
                                      {renderFirstTimeBadge(r)}
                                      {r.gender === '男' && <span className="text-xs bg-blue-100 text-blue-800 px-1.5 rounded whitespace-nowrap">男</span>}
                                      {r.gender === '女' && <span className="text-xs bg-pink-100 text-pink-800 px-1.5 rounded whitespace-nowrap">女</span>}
                                    </div>
                                    <div className="text-sm text-gray-600 font-medium leading-5 [word-break:keep-all]">{r.church}</div>
                                    {r.submissionDate && formatSubmissionTime(r.submissionDate) && (
                                      <div className="text-xs text-gray-400 mt-1 leading-5">
                                        <span className="whitespace-nowrap">報名時間：</span>
                                        <span className="whitespace-nowrap tabular-nums">{formatSubmissionTime(r.submissionDate)}</span>
                                      </div>
                                    )}
                                    {renderHistoryBadge(r)}
                                  </div>
                                </td>

                                {/* Contact */}
                                <td className={`${TABLE_BODY_CELL} font-mono text-[15px] text-gray-600 tracking-wide whitespace-nowrap tabular-nums`}>
                                  {r.phone}
                                </td>

                                {/* Role(s) - 支援多選 */}
                                <td className={TABLE_BODY_CELL}>
                                  <div className="flex flex-wrap gap-1.5">
                                    {(r.roles && r.roles.length > 0 ? r.roles : [r.role]).map((role, idx) => (
                                      <span key={idx} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md border border-indigo-100 font-medium inline-block leading-5 whitespace-nowrap [word-break:keep-all]">
                                        {role.split('~')[0].split('(')[0]}
                                        {role === '學生' && r.studentSpecialization && (
                                          <span className="ml-1 text-indigo-600">({r.studentSpecialization})</span>
                                        )}
                                        {role === '西醫' && r.westernMedicineSpecialty && (
                                          <span className="ml-1 text-indigo-600">({r.westernMedicineSpecialty})</span>
                                        )}
                                        {role === '治療師' && r.therapistSpecialty && (
                                          <span className="ml-1 text-indigo-600">({r.therapistSpecialty})</span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                  {((r.roles && r.roles.some(role => role.includes('其他'))) || r.role.includes('其他')) && (
                                    <div className="text-xs text-gray-500 mt-1 max-w-[220px] truncate">{r.otherRoleDescription}</div>
                                  )}
                                  {r.message && (
                                    <div className="text-xs text-gray-400 mt-1 flex items-center group/msg cursor-help relative min-w-0">
                                      <HelpCircle size={12} className="mr-1" />
                                      <span className="truncate max-w-[200px]">{r.message}</span>
                                      <div className="absolute left-0 top-full mt-1 z-20 bg-gray-800 text-white text-xs p-2 rounded w-48 opacity-0 group-hover/msg:opacity-100 pointer-events-none transition-opacity shadow-lg">
                                        {r.message}
                                      </div>
                                    </div>
                                  )}
                                  {renderOfficialSupportInline(r)}
                                </td>

                                {/* Leadership Dropdown */}
                                <td className="px-3 py-3 align-middle bg-indigo-50/30">
                                  <select
                                    className={`${ASSIGNMENT_SELECT_CLASS}
                                      ${r.currentLeadership !== '無'
                                        ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-300'
                                      }`}
                                    value={r.currentLeadership}
                                    onChange={(e) => handleAssignmentChange(r.id, 'leadershipRole', e.target.value)}
                                  >
                                    {LEADERSHIP_ROLES.map(role => (
                                      <option key={role} value={role} className="bg-white text-gray-900">
                                        {role}
                                      </option>
                                    ))}
                                  </select>
                                </td>

                                {/* Task Dropdown (With conditional secondary dropdown) */}
                                <td className="px-3 py-3 align-middle bg-indigo-50/30">
                                  <div className="flex flex-col gap-2">
                                    <select
                                      className={`${ASSIGNMENT_SELECT_CLASS}
                                      ${r.currentTask !== '尚未分配'
                                          ? 'bg-white text-indigo-800 border-indigo-300 ring-1 ring-indigo-100'
                                          : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-300'
                                        }`}
                                      value={r.currentTask}
                                      onChange={(e) => handleAssignmentChange(r.id, 'taskRole', e.target.value)}
                                    >
                                      {taskOptions.map(role => (
                                        <option key={role} value={role} className="bg-white text-gray-900">
                                          {role}
                                        </option>
                                      ))}
                                    </select>

                                    {r.currentTask === '跟診' && (
                                      <select
                                        className="appearance-none w-full min-h-10 px-3 py-1.5 rounded-lg text-sm font-medium border bg-yellow-50 border-yellow-200 text-yellow-800 focus:outline-none focus:ring-2 focus:ring-yellow-200 text-center cursor-pointer animate-fade-in whitespace-nowrap"
                                        value={r.currentSubTask}
                                        onChange={(e) => handleAssignmentChange(r.id, 'taskSubDetail', e.target.value)}
                                      >
                                        <option value="">選擇跟診站點...</option>
                                        {stationTargetOptions.map(station => (
                                          <option key={station} value={station}>{station}</option>
                                        ))}
                                      </select>
                                    )}

                                    {r.currentTask === '流動' && (
                                      <select
                                        className="appearance-none w-full px-2 py-1.5 rounded-lg text-sm font-medium border bg-blue-50 border-blue-200 text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-200 text-center cursor-pointer animate-fade-in"
                                        value={r.currentSubTask}
                                        onChange={(e) => handleAssignmentChange(r.id, 'taskSubDetail', e.target.value)}
                                      >
                                        <option value="">選擇流動站點...</option>
                                        {stationTargetOptions.map(station => (
                                          <option key={station} value={station}>{station}</option>
                                        ))}
                                      </select>
                                    )}

                                    {taskSupportsAssistTarget(r.currentTask) && (
                                      <select
                                        className="appearance-none w-full px-2 py-1.5 rounded-lg text-sm font-medium border bg-violet-50 border-violet-200 text-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-200 text-center cursor-pointer animate-fade-in"
                                        value={r.currentAssistTarget}
                                        onChange={(e) => handleAssignmentChange(r.id, 'assistTarget', e.target.value)}
                                      >
                                        <option value="">{isDentalAssistantTask(r.currentTask) ? '選擇牙助對象...' : '選擇跟診對象...'}</option>
                                        {getAssistTargetSelectOptions(r).map(name => (
                                          <option key={name} value={name}>{name}</option>
                                        ))}
                                      </select>
                                    )}

                                    {/* Custom task input when 其他 selected */}
                                    {r.currentTask === '其他' && (
                                      <div className="flex gap-2 animate-fade-in">
                                        <input
                                          type="text"
                                          className="min-w-0 flex-1 px-2 py-1.5 rounded-lg text-sm font-medium border bg-emerald-50 border-emerald-200 text-emerald-800 placeholder-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 text-center"
                                          value={customTaskInput[r.id] ?? r.currentSubTask}
                                          placeholder="輸入自訂職位..."
                                          onChange={(e) => setCustomTaskInput(prev => ({ ...prev, [r.id]: e.target.value }))}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddCustomTask(r.id);
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleAddCustomTask(r.id)}
                                          disabled={!(customTaskInput[r.id] ?? r.currentSubTask).trim()}
                                          className="shrink-0 p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-200 disabled:cursor-not-allowed transition-colors"
                                          title="加入自訂職位"
                                        >
                                          <Save size={16} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>

                                {/* Row actions */}
                                <td className={`${TABLE_BODY_CELL} text-center`}>
                                  <div className="flex flex-col items-center justify-center gap-1">
                                    <button
                                      onClick={() => startEditMember(r)}
                                      className="text-gray-400 hover:text-indigo-600 p-2 rounded-full hover:bg-indigo-50 transition-all hover:scale-110"
                                      title="修改基本資料"
                                    >
                                      <Pencil size={20} />
                                    </button>
                                    {!r.isEligible && (
                                    <button
                                      onClick={() => handleManualOverride(r.id)}
                                      className="text-gray-400 hover:text-indigo-600 p-2 rounded-full hover:bg-indigo-50 transition-all hover:scale-110"
                                      title="手動標記為合格 (覆核)"
                                    >
                                      <UserCheck size={20} />
                                    </button>
                                    )}
                                    <button
                                      onClick={() => handleRemoveRegistrantFromSelectedEvent(r.id)}
                                      className="text-gray-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-all hover:scale-110"
                                      title="從本梯次移除"
                                    >
                                      <Trash2 size={20} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* B. Mobile / Tablet View: Card List */}
                    <div className="xl:hidden grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                      {filteredData.map(r => (
                        <div key={r.id} className={`bg-white rounded-2xl shadow-md overflow-hidden transition-all h-full flex flex-col ${
                          !r.isApproved
                            ? 'border-2 border-orange-300 shadow-orange-100'
                            : 'border border-gray-200'
                        }`}>
                          {/* Card Header: Status & Name */}
                          <div className={`px-4 py-3 border-b min-h-[106px] flex justify-between items-start gap-3 ${
                            r.isApproved ? 'bg-white' : 'bg-orange-50'
                          }`}>
                            <div className="flex items-start gap-2 min-w-0">
                              {r.isEligible && !r.isWatchlisted ? (
                                <CheckCircle size={20} className="text-green-500 mt-0.5 shrink-0" />
                              ) : r.isWatchlisted ? (
                                <ShieldAlert size={20} className="text-orange-500 animate-pulse mt-0.5 shrink-0" />
                              ) : (
                                <ShieldAlert size={20} className="text-red-500 mt-0.5 shrink-0" />
                              )}
                              <div className="flex flex-col min-w-0">
                                <span className="text-lg font-bold text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1 leading-6">
                                  <span className="whitespace-nowrap [word-break:keep-all]">{r.name}</span>
                                  {r.gender && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${r.gender === '男' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'}`}>{r.gender}</span>
                                  )}
                                  {renderFirstTimeBadge(r)}
                                </span>
                                <span className={`text-xs leading-5 [word-break:keep-all] ${r.church ? 'text-gray-500' : 'text-gray-300'}`}>
                                  {r.church || '未填教會'}
                                </span>
                                <span className="text-[11px] text-gray-400 leading-5">
                                  <span className="whitespace-nowrap">報名時間：</span>
                                  <span className="whitespace-nowrap tabular-nums">{formatSubmissionTime(r.submissionDate) || EMPTY_FIELD_LABEL}</span>
                                </span>
                                {renderHistoryBadge(r)}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => startEditMember(r)}
                                className="p-2 bg-gray-100 text-gray-500 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                title="修改基本資料"
                              >
                                <Pencil size={16} />
                              </button>
                              {!r.isEligible && (
                                <button
                                  onClick={() => handleManualOverride(r.id)}
                                  className="p-2 bg-gray-100 text-gray-500 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                  title="手動標記為符合資格"
                                >
                                  <UserCheck size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveRegistrantFromSelectedEvent(r.id)}
                                className="p-2 bg-gray-100 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                                title="從本梯次移除"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {/* 審核狀態按鈕 - 醒目顯示 */}
                          <div className={`px-4 py-3 ${!r.isApproved ? 'bg-orange-50' : 'bg-white'}`}>
                            <button
                              onClick={() => handleApprovalToggle(r.id, r.isApproved)}
                              className={`w-full py-3 px-4 rounded-xl font-bold text-sm transition-all active:scale-95 shadow-sm ${
                                r.isApproved
                                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                  : 'bg-gradient-to-r from-orange-400 to-orange-500 text-white border-2 border-orange-600 shadow-lg animate-pulse hover:from-orange-500 hover:to-orange-600'
                              }`}
                            >
                              {r.isApproved ? '✓ 已通過審核' : '⚠️ 待審核 - 點擊通過'}
                            </button>
                          </div>

                          {/* Card Body: Info */}
                          <div className="p-4 space-y-3 flex-1">
                            <div className="min-h-8 flex justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex items-center shrink-0"><Stethoscope size={14} className="mr-1" /> 醫宣角色</span>
                              <div className="flex flex-wrap gap-1 justify-end min-w-0">
                                {getDisplayRoles(r).length > 0 ? (
                                  getDisplayRoles(r).map((role, idx) => (
                                    <span key={idx} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-medium leading-5 whitespace-nowrap [word-break:keep-all]">
                                      {role.split('~')[0]}
                                      {role === '學生' && r.studentSpecialization && (
                                        <span className="ml-1">({r.studentSpecialization})</span>
                                      )}
                                      {role === '西醫' && r.westernMedicineSpecialty && (
                                        <span className="ml-1">({r.westernMedicineSpecialty})</span>
                                      )}
                                      {role === '治療師' && r.therapistSpecialty && (
                                        <span className="ml-1">({r.therapistSpecialty})</span>
                                      )}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs bg-gray-50 text-gray-400 px-2 py-0.5 rounded font-medium leading-5 whitespace-nowrap">{EMPTY_FIELD_LABEL}</span>
                                )}
                              </div>
                            </div>
                            <div className="min-h-8 flex justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex items-center shrink-0"><Phone size={14} className="mr-1" /> 電話</span>
                              <span className={`font-mono tracking-wide whitespace-nowrap tabular-nums ${r.phone ? 'text-gray-800' : 'text-gray-300'}`}>
                                {r.phone || EMPTY_FIELD_LABEL}
                              </span>
                            </div>
                            {/* 報備支援狀態：只顯示給需報備的醫事角色 */}
                            {shouldDisplayOfficialSupport(r) && (
                              <div className={`min-h-[58px] flex flex-col sm:flex-row sm:justify-between gap-2 text-sm sm:items-center px-3 py-2 rounded-lg border ${
                                r.canProvideOfficialSupport !== undefined ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'
                              }`}>
                                <span className={`flex items-center font-bold ${r.canProvideOfficialSupport !== undefined ? 'text-amber-800' : 'text-gray-400'}`}>
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  報備支援
                                </span>
                                {(() => {
                                  const officialSupportStatus = getOfficialSupportStatus(r);

                                  if (officialSupportStatus.type === 'yes') {
                                    return (
                                      <div className="flex items-start sm:justify-end gap-2 text-left sm:text-right">
                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white shrink-0">
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        </div>
                                        <div>
                                          <div className="text-green-700 font-bold text-sm">可報備</div>
                                          {officialSupportStatus.detail && (
                                            <div className="text-green-800 font-bold text-xs leading-5 [word-break:keep-all]">
                                              {officialSupportStatus.detail}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }

                                  if (officialSupportStatus.type === 'no') {
                                    return (
                                      <div className="flex items-center">
                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-400 text-white mr-1.5">
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                          </svg>
                                        </div>
                                        <span className="text-gray-600 font-bold text-sm">無報備</span>
                                      </div>
                                    );
                                  }

                                  return <span className="text-gray-400 font-bold text-sm">{EMPTY_FIELD_LABEL}</span>;
                                })()}
                              </div>
                            )}
                            <div className={`min-h-11 p-2 rounded text-xs flex items-start ${r.message ? 'bg-gray-50 text-gray-500' : 'bg-gray-50/60 text-gray-300'}`}>
                              <HelpCircle size={12} className="mr-1 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{r.message || '無備註'}</span>
                            </div>
                          </div>

                          {/* Card Footer: Actions (Dropdowns) */}
                          <div className="bg-indigo-50/40 p-4 border-t border-indigo-50 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="flex flex-col">
                              <label className="text-xs font-bold text-indigo-800 mb-1 ml-1">職務 (Leadership)</label>
                              <select
                                className={`w-full min-h-11 px-3 py-2 rounded-lg text-sm font-bold leading-5 border transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer appearance-none text-center whitespace-nowrap
                                      ${r.currentLeadership !== '無'
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300'
                                  }`}
                                value={r.currentLeadership}
                                onChange={(e) => handleAssignmentChange(r.id, 'leadershipRole', e.target.value)}
                              >
                                {LEADERSHIP_ROLES.map(role => (
                                  <option key={role} value={role} className="bg-white text-gray-900">
                                    {role}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="flex flex-col">
                              <label className="text-xs font-bold text-indigo-800 mb-1 ml-1">任務 (Task)</label>
                              <select
                                className={`w-full min-h-11 px-3 py-2 rounded-lg text-sm font-bold leading-5 border transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer appearance-none text-center whitespace-nowrap
                                      ${r.currentTask !== '尚未分配'
                                    ? 'bg-white text-indigo-800 border-indigo-300 ring-1 ring-indigo-100'
                                    : 'bg-white text-gray-700 border-gray-300'
                                  }`}
                                value={r.currentTask}
                                onChange={(e) => handleAssignmentChange(r.id, 'taskRole', e.target.value)}
                              >
                                {taskOptions.map(role => (
                                  <option key={role} value={role} className="bg-white text-gray-900">
                                    {role}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Mobile Subtask Selector */}
                            {r.currentTask === '跟診' && (
                              <div className="sm:col-span-2 animate-fade-in">
                                <select
                                  className="w-full min-h-11 px-3 py-2 rounded-lg text-sm font-bold border bg-yellow-50 border-yellow-200 text-yellow-800 focus:outline-none focus:ring-2 focus:ring-yellow-200 text-center cursor-pointer whitespace-nowrap"
                                  value={r.currentSubTask}
                                  onChange={(e) => handleAssignmentChange(r.id, 'taskSubDetail', e.target.value)}
                                >
                                  <option value="">選擇跟診站點...</option>
                                  {stationTargetOptions.map(station => (
                                    <option key={station} value={station}>{station}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {r.currentTask === '流動' && (
                              <div className="col-span-2 animate-fade-in">
                                <select
                                  className="w-full px-2 py-2 rounded-lg text-sm font-bold border bg-blue-50 border-blue-200 text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-200 text-center cursor-pointer"
                                  value={r.currentSubTask}
                                  onChange={(e) => handleAssignmentChange(r.id, 'taskSubDetail', e.target.value)}
                                >
                                  <option value="">選擇流動站點...</option>
                                  {stationTargetOptions.map(station => (
                                    <option key={station} value={station}>{station}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {taskSupportsAssistTarget(r.currentTask) && (
                              <div className="col-span-2 animate-fade-in">
                                <select
                                  className="w-full px-2 py-2 rounded-lg text-sm font-bold border bg-violet-50 border-violet-200 text-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-200 text-center cursor-pointer"
                                  value={r.currentAssistTarget}
                                  onChange={(e) => handleAssignmentChange(r.id, 'assistTarget', e.target.value)}
                                >
                                  <option value="">{isDentalAssistantTask(r.currentTask) ? '選擇牙助對象...' : '選擇跟診對象...'}</option>
                                  {getAssistTargetSelectOptions(r).map(name => (
                                    <option key={name} value={name}>{name}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* Mobile Custom Task Input */}
                            {r.currentTask === '其他' && (
                              <div className="sm:col-span-2 animate-fade-in">
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    className="min-w-0 flex-1 px-3 py-2 rounded-lg text-sm font-bold border bg-emerald-50 border-emerald-200 text-emerald-800 placeholder-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 text-center"
                                    value={customTaskInput[r.id] ?? r.currentSubTask}
                                    placeholder="輸入自訂職位..."
                                    onChange={(e) => setCustomTaskInput(prev => ({ ...prev, [r.id]: e.target.value }))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleAddCustomTask(r.id);
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleAddCustomTask(r.id)}
                                    disabled={!(customTaskInput[r.id] ?? r.currentSubTask).trim()}
                                    className="shrink-0 p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-200 disabled:cursor-not-allowed transition-colors"
                                    title="加入自訂職位"
                                  >
                                    <Save size={16} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {filteredData.length === 0 && (
                        <div className="p-8 text-center text-gray-400 bg-white rounded-2xl border border-dashed">
                          無資料
                        </div>
                      )}
                    </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'events' && (
          <div className="max-w-5xl mx-auto animate-fade-in-up">
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200">
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h2 className="text-2xl font-bold text-gray-900">新增/管理梯隊 ({dashboardYear})</h2>
                <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  目前只顯示 {dashboardYear} 年度的梯隊
                </div>
              </div>
              <form onSubmit={handleAddEvent} className="space-y-4 mb-8">
                {/* Event Date Range */}
                <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
                  <label className="block text-sm font-bold text-blue-900 mb-3">梯隊日期</label>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[100px]">
                      <label className="block text-xs text-blue-700 mb-1.5 font-semibold">月份</label>
                      <select
                        className="w-full border-2 border-blue-300 p-2.5 rounded-lg bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none font-semibold"
                        value={dateInput.month}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, month: val }));
                          updateDateRange(val, dateInput.startDay, dateInput.endDay);
                        }}
                      >
                        <option value="">選擇月份</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                          <option key={m} value={m}>{m}月</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[100px]">
                      <label className="block text-xs text-blue-700 mb-1.5 font-semibold">開始日</label>
                      <select
                        className="w-full border-2 border-blue-300 p-2.5 rounded-lg bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none font-semibold"
                        value={dateInput.startDay}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, startDay: val }));
                          updateDateRange(dateInput.month, val, dateInput.endDay);
                        }}
                      >
                        <option value="">開始</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}日</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[100px]">
                      <label className="block text-xs text-blue-700 mb-1.5 font-semibold">結束日 (選填)</label>
                      <select
                        className="w-full border-2 border-blue-300 p-2.5 rounded-lg bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none font-semibold"
                        value={dateInput.endDay}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, endDay: val }));
                          updateDateRange(dateInput.month, dateInput.startDay, val);
                        }}
                      >
                        <option value="">結束 (同天免填)</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}日</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-[2] min-w-[200px]">
                      <label className="block text-xs text-blue-700 mb-1.5 font-semibold">預覽格式</label>
                      <div className="border-2 border-blue-300 p-2.5 rounded-lg bg-white font-bold text-indigo-700 text-center">
                        {newEvent.dateRange || '尚未選擇'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">梯隊地點</label>
                  <input
                    placeholder="例如：東南區"
                    className="w-full border-2 border-gray-300 p-3 rounded-xl text-base focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    value={newEvent.location}
                    onChange={e => setNewEvent({ ...newEvent, location: e.target.value })}
                  />
                </div>

                {/* Deadline */}
                <div className="bg-red-50 p-4 rounded-xl border-2 border-red-200">
                  <label className="block text-sm font-bold text-red-900 mb-1">報名截止日</label>
                  <p className="text-xs text-red-700 mb-3">選好梯隊日期後，自動帶入出隊前一個月；可再自行調整。</p>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-red-700 mb-1.5 font-semibold">月份</label>
                      <select
                        className="w-full border-2 border-red-300 p-2.5 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none font-semibold"
                        value={dateInput.deadlineMonth}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, deadlineMonth: val }));
                          updateDeadline(val, dateInput.deadlineDay);
                        }}
                      >
                        <option value="">選擇月份</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                          <option key={m} value={m}>{m}月</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-red-700 mb-1.5 font-semibold">日期</label>
                      <select
                        className="w-full border-2 border-red-300 p-2.5 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none font-semibold"
                        value={dateInput.deadlineDay}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, deadlineDay: val }));
                          updateDeadline(dateInput.deadlineMonth, val);
                        }}
                      >
                        <option value="">選擇日期</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}日</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-[2] min-w-[200px]">
                      <label className="block text-xs text-red-700 mb-1.5 font-semibold">預覽格式</label>
                      <div className="border-2 border-red-300 p-2.5 rounded-lg bg-white font-bold text-red-700 text-center">
                        {newEvent.deadline || '尚未選擇'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cancel Deadline */}
                <div className="bg-amber-50 p-4 rounded-xl border-2 border-amber-200">
                  <label className="block text-sm font-bold text-amber-900 mb-1">線上取消截止日</label>
                  <p className="text-xs text-amber-700 mb-3">過了這一天，報名者無法在網站上自行取消，需聯絡醫宣幹部。預設為出隊前一個月。</p>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-amber-700 mb-1.5 font-semibold">月份</label>
                      <select
                        className="w-full border-2 border-amber-300 p-2.5 rounded-lg bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none font-semibold"
                        value={dateInput.cancelMonth}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, cancelMonth: val }));
                          updateCancelDeadline(val, dateInput.cancelDay);
                        }}
                      >
                        <option value="">選擇月份</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                          <option key={m} value={m}>{m}月</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-amber-700 mb-1.5 font-semibold">日期</label>
                      <select
                        className="w-full border-2 border-amber-300 p-2.5 rounded-lg bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none font-semibold"
                        value={dateInput.cancelDay}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDateInput(prev => ({ ...prev, cancelDay: val }));
                          updateCancelDeadline(dateInput.cancelMonth, val);
                        }}
                      >
                        <option value="">選擇日期</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}日</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-[2] min-w-[200px]">
                      <label className="block text-xs text-amber-700 mb-1.5 font-semibold">預覽格式</label>
                      <div className="border-2 border-amber-300 p-2.5 rounded-lg bg-white font-bold text-amber-800 text-center">
                        {newEvent.cancelDeadline || '尚未選擇'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">活動類型</label>
                  <select
                    className="w-full border-2 border-gray-300 p-3 rounded-xl text-base focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    value={newEvent.type}
                    onChange={e => setNewEvent({ ...newEvent, type: e.target.value as EventType })}
                  >
                    <option value="mission">義診梯隊</option>
                    <option value="assembly">年度聚會</option>
                  </select>
                </div>

                {/* Capacity */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">人數上限（選填）</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="不填則不限人數"
                    className="w-full border-2 border-gray-300 p-3 rounded-xl text-base focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    value={newEvent.capacity}
                    onChange={e => setNewEvent({ ...newEvent, capacity: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">只計算已核准的報名人數，達到上限時系統將自動關閉報名</p>
                </div>

                <button type="submit" className="w-full bg-indigo-600 text-white px-6 py-4 rounded-xl hover:bg-indigo-700 text-lg font-bold shadow-md transition-all active:scale-95 flex justify-center items-center btn-press">
                  <Plus size={24} className="mr-2" /> 新增梯隊
                </button>
              </form>
              <ul className="space-y-4">
                {dashboardEvents.map(ev => {
                  const isEditing = editingEventId === ev.id;

                  return (
                    <li key={ev.id} className={`flex flex-col md:flex-row justify-between items-center p-4 rounded-xl border transition-shadow ${isEditing ? 'bg-indigo-50 border-indigo-200 shadow-md' : 'bg-gray-50 border-gray-200 hover:shadow-md'}`}>
                      {isEditing && editForm ? (
                        // Edit Mode
                        <div className="w-full space-y-4">
                          <div className="flex flex-col md:flex-row gap-3 w-full">
                            <div className="flex-1">
                              <label className="text-xs font-bold text-indigo-600 ml-1 mb-1 block">日期</label>
                              <input
                                className="w-full border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none"
                                value={editForm.dateRange}
                                onChange={e => setEditForm({ ...editForm, dateRange: e.target.value })}
                              />
                            </div>
                            <div className="flex-[2]">
                              <label className="text-xs font-bold text-indigo-600 ml-1 mb-1 block">地點</label>
                              <input
                                className="w-full border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none"
                                value={editForm.location}
                                onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                              />
                            </div>
                            <div className="flex-1 md:max-w-[150px]">
                              <label className="text-xs font-bold text-indigo-600 ml-1 mb-1 block">報名截止日</label>
                              <input
                                type="date"
                                className="w-full border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none"
                                value={editForm.deadlineDate || ''}
                                onChange={e => {
                                  const iso = e.target.value;
                                  const d = iso ? new Date(`${iso}T00:00:00`) : null;
                                  setEditForm({ ...editForm, deadlineDate: iso, deadline: d ? toMonthDayLabel(d) : '' });
                                }}
                              />
                            </div>
                            <div className="flex-1 md:max-w-[170px]">
                              <label className="text-xs font-bold text-indigo-600 ml-1 mb-1 block">線上取消截止日</label>
                              <input
                                type="date"
                                className="w-full border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none"
                                value={editForm.cancelDeadlineDate || (() => { const c = getCancelDeadlineDate(editForm); return c ? toIsoDate(c) : ''; })()}
                                onChange={e => {
                                  const iso = e.target.value;
                                  const d = iso ? new Date(`${iso}T00:00:00`) : null;
                                  setEditForm({ ...editForm, cancelDeadlineDate: iso || undefined, cancelDeadline: d ? toMonthDayLabel(d) : undefined });
                                }}
                              />
                              <p className="text-[11px] text-gray-500 mt-1 ml-1">未設定時為出隊前一個月</p>
                            </div>
                            <div className="flex-1 md:max-w-[150px]">
                              <label className="text-xs font-bold text-indigo-600 ml-1 mb-1 block">人數上限</label>
                              <input
                                type="number"
                                min="0"
                                placeholder="不限"
                                className="w-full border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none"
                                value={editForm.capacity || ''}
                                onChange={e => setEditForm({ ...editForm, capacity: e.target.value ? parseInt(e.target.value) : undefined })}
                              />
                            </div>
                            <div className="flex-1 md:max-w-[150px]">
                              <label className="text-xs font-bold text-indigo-600 ml-1 mb-1 block">活動類型</label>
                              <select
                                className="w-full border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none"
                                value={editForm.type || 'mission'}
                                onChange={e => setEditForm({ ...editForm, type: e.target.value as EventType })}
                              >
                                <option value="mission">義診梯隊</option>
                                <option value="assembly">年度聚會</option>
                              </select>
                            </div>
                          </div>

                          {/* Schedule Editor */}
                          <div className="w-full border-t border-indigo-200 pt-4">
                            <ScheduleEditor
                              schedule={editForm.schedule || []}
                              onChange={(schedule) => setEditForm({ ...editForm, schedule })}
                            />
                          </div>

                          <div className="flex gap-2 justify-end pt-3 border-t border-indigo-200">
                            <button onClick={saveEditingEvent} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2" title="儲存">
                              <Save size={18} />
                              儲存所有變更
                            </button>
                            <button onClick={cancelEditingEvent} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors shadow-sm flex items-center gap-2" title="取消">
                              <X size={18} />
                              取消
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          <div className="flex-grow text-center md:text-left mb-3 md:mb-0 w-full md:w-auto">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-lg md:text-xl text-gray-800 font-medium">{ev.dateRange} - {ev.location}</span>
                              {ev.schedule && ev.schedule.length > 0 && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                  有行程 ({ev.schedule.length}天)
                                </span>
                              )}
                              {ev.registrationClosed && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-bold">
                                  已關閉報名
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="text-sm text-red-500 font-bold">截止日: {ev.deadline}</span>
                              <span className="text-sm text-amber-700 font-bold">取消截止: {(() => { const c = getCancelDeadlineDate(ev); return c ? toMonthDayLabel(c) : '未設定'; })()}{ev.cancelDeadlineDate ? '' : '（預設）'}</span>
                              <span className="text-sm text-gray-600">
                                <Users size={14} className="inline mr-1 mb-0.5" />
                                已核准: {approvedCountByEvent[ev.id] || 0}{ev.capacity ? ` / ${ev.capacity} 人` : ' 人（不限）'}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 w-full md:w-auto justify-center md:justify-end">
                            <button
                              onClick={() => {
                                const updated = { ...ev, registrationClosed: !ev.registrationClosed };
                                onEditEvent(updated);
                                showToast(
                                  ev.registrationClosed ? 'success' : 'info',
                                  ev.registrationClosed ? '已重新開放報名' : '已關閉報名',
                                  `${ev.dateRange} - ${ev.location}`
                                );
                              }}
                              className={`p-3 rounded-lg shadow-sm border transition-colors hover:scale-110 ${
                                ev.registrationClosed
                                  ? 'text-green-600 hover:text-green-700 border-green-200 bg-white hover:bg-green-50'
                                  : 'text-orange-500 hover:text-orange-700 border-orange-200 bg-white hover:bg-orange-50'
                              }`}
                              title={ev.registrationClosed ? '重新開放報名' : '關閉報名'}
                            >
                              {ev.registrationClosed ? <Unlock size={20} /> : <Lock size={20} />}
                            </button>
                            <button onClick={() => startEditingEvent(ev)} className="text-indigo-500 hover:text-indigo-700 bg-white p-3 rounded-lg shadow-sm border border-gray-200 hover:bg-indigo-50 transition-colors hover:scale-110" title="編輯">
                              <Pencil size={20} />
                            </button>
                            <button onClick={() => handleDeleteEvent(ev.id)} className="text-red-500 hover:text-red-700 bg-white p-3 rounded-lg shadow-sm border border-gray-200 hover:bg-red-50 transition-colors hover:scale-110" title="刪除">
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'outlook-records' && (
          <OutlookRecords
            events={dashboardEvents}
            systemYear={dashboardYear}
            showToast={showToast}
          />
        )}

        {activeTab === 'inventory' && (
          <InventoryManager
            events={dashboardEvents}
            systemYear={dashboardYear}
            currentUserLabel={currentUserLabel}
            showToast={showToast}
            canManageAccessCodes
          />
        )}

        {activeTab === 'settings' && (
          <div className="max-w-6xl mx-auto space-y-8 animate-fade-in-up">

            {/* 1. System Year Management */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200">
              <div className="flex items-center mb-6 border-b pb-4">
                <RotateCw className="text-indigo-600 mr-3" size={28} />
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">系統年度設定</h2>
                </div>
              </div>
              <div className="flex items-end gap-4 max-w-md">
                <div className="flex-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2">目前運作年度</label>
                  <input
                    type="number"
                    className="w-full border-2 border-gray-300 p-3 rounded-xl text-xl font-bold focus:border-indigo-500 outline-none"
                    value={yearInput}
                    onChange={(e) => setYearInput(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => onUpdateSystemYear(parseInt(yearInput))}
                  className="bg-indigo-600 text-white px-6 py-3.5 rounded-xl font-bold hover:bg-indigo-700 shadow-sm active:scale-95 transition-all"
                >
                  切換年度
                </button>
              </div>
              <p className="mt-4 text-sm text-gray-500 bg-yellow-50 p-3 rounded-lg border border-yellow-100 inline-block">
                <AlertTriangle size={14} className="inline mr-1 mb-0.5" />
                注意：切換年度後，前台網頁將只顯示該年度的梯隊與報名表。舊年度資料會被保留但隱藏。
              </p>
            </div>

            {/* 2. Admin Management Section */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200">
              <div className="flex items-center mb-6 border-b pb-4">
                <Shield className="text-indigo-600 mr-3" size={28} />
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">權限管理</h2>
                  <p className="text-gray-500 text-sm mt-1">管理後台管理員與物資填寫人員的 Google 帳號</p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <input
                  type="email"
                  placeholder="請輸入 Gmail 信箱..."
                  className="flex-1 border-2 border-gray-300 p-3 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                />
                <button
                  onClick={handleAddAdmin}
                  disabled={!newAdminEmail.includes('@')}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 shadow-sm active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                >
                  新增人員
                </button>
              </div>

              {loadingAdmins ? (
                <div className="text-gray-500 italic">載入中...</div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-3 text-sm font-black text-gray-800">管理員</h3>
                    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                      {adminEmails.length > 0 ? (
                        <ul className="divide-y divide-gray-200">
                          {adminEmails.map(email => (
                            <li key={email} className="flex justify-between items-center p-4 hover:bg-white transition-colors">
                              <div className="flex items-center font-bold text-gray-700 text-sm md:text-base break-all">
                                <User size={18} className="mr-3 text-gray-400 flex-shrink-0" /> {email}
                              </div>
                              <button
                                onClick={() => handleRemoveAdmin(email)}
                                className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors text-sm font-bold flex items-center whitespace-nowrap ml-2"
                              >
                                <Trash2 size={16} className="mr-1" /> 移除
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="p-6 text-center text-gray-500 italic">目前沒有額外的管理員設定</div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="mb-2 text-sm font-black text-gray-800">物資填寫人員</h3>
                    <p className="mb-4 text-sm text-gray-500">這些帳號只能登入物資管理平台，不能進入名單、梯隊或系統設定。</p>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <input
                        type="email"
                        placeholder="請輸入物資填寫人員 Gmail..."
                        className="flex-1 border-2 border-gray-300 p-3 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        value={newInventoryEmail}
                        onChange={(e) => setNewInventoryEmail(e.target.value)}
                      />
                      <button
                        onClick={handleAddInventoryUser}
                        disabled={!newInventoryEmail.includes('@')}
                        className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 shadow-sm active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                      >
                        新增物資人員
                      </button>
                    </div>
                    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                      {inventoryEmails.length > 0 ? (
                        <ul className="divide-y divide-gray-200">
                          {inventoryEmails.map(email => (
                            <li key={email} className="flex justify-between items-center p-4 hover:bg-white transition-colors">
                              <div className="flex items-center font-bold text-gray-700 text-sm md:text-base break-all">
                                <Package size={18} className="mr-3 text-gray-400 flex-shrink-0" /> {email}
                              </div>
                              <button
                                onClick={() => handleRemoveInventoryUser(email)}
                                className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors text-sm font-bold flex items-center whitespace-nowrap ml-2"
                              >
                                <Trash2 size={16} className="mr-1" /> 移除
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="p-6 text-center text-gray-500 italic">目前沒有物資填寫人員設定</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 3. Watchlist Management Section */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200">
              <div className="flex items-center mb-6 border-b pb-4">
                <ShieldAlert className="text-orange-600 mr-3" size={28} />
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">觀察名單</h2>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-xl text-yellow-900 text-sm mb-6 border border-yellow-200">
                <p className="font-bold mb-2 flex items-center"><AlertTriangle size={16} className="mr-1" /> 功能說明</p>
                <p>在此名單中的人員，報名後會被標記為「待審核」，需管理員手動核准後才會顯示在公開名單。適用於特殊狀況或需要額外確認的人員。</p>
              </div>

              <div className="flex flex-col gap-4 mb-6">
                <input
                  type="text"
                  placeholder="請輸入姓名"
                  className="w-full border-2 border-gray-300 p-3 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  value={newWatchlistName}
                  onChange={(e) => setNewWatchlistName(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="請輸入教會"
                  className="w-full border-2 border-gray-300 p-3 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  value={newWatchlistChurch}
                  onChange={(e) => setNewWatchlistChurch(e.target.value)}
                />
                <button
                  onClick={handleAddWatchlistItem}
                  disabled={!newWatchlistName || !newWatchlistChurch}
                  className="w-full bg-orange-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-700 shadow-sm active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  新增人員
                </button>
              </div>

              {loadingWatchlist ? (
                <div className="text-gray-500 italic">載入中...</div>
              ) : (
                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  {watchlistItems.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {watchlistItems.map(item => (
                        <li key={item.id} className="flex justify-between items-center p-4 hover:bg-white transition-colors gap-3">
                          {/* 修復手機版溢出：姓名區域使用 flex-1 和 min-w-0 防止溢出 */}
                          <div className="flex items-center flex-1 min-w-0 font-bold text-gray-700 text-sm md:text-base">
                            <User size={18} className="mr-3 text-orange-400 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{item.name}</div>
                              <div className="text-xs text-gray-400 font-normal truncate">{item.church}</div>
                            </div>
                          </div>
                          {/* 修復手機版溢出：移除按鈕改為圖標樣式 */}
                          <button
                            onClick={() => handleRemoveWatchlistItem(item)}
                            className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors flex-shrink-0 flex items-center justify-center"
                            title="移除"
                          >
                            <Trash2 size={18} className="md:mr-1" />
                            <span className="hidden md:inline text-sm font-bold ml-1">移除</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-6 text-center text-gray-500 italic">目前沒有觀察名單人員</div>
                  )}
                </div>
              )}
            </div>

            {/* 4. Historical Data Import Section */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200">
              <div className="flex items-center mb-6 border-b pb-4">
                <Database className="text-indigo-600 mr-3" size={28} />
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">動員資格資料庫</h2>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm md:text-base">
                    系統依據此資料庫自動比對報名人員是否參加過動員聚會。請將目前的 Excel/Google Sheet 名單匯出為 CSV 後上傳至此。
                  </p>
                  <div className="bg-blue-50 p-4 rounded-xl text-blue-900 text-sm mb-6 border border-blue-100">
                    <p className="font-bold mb-2 flex items-center"><HelpCircle size={16} className="mr-1" /> 如何轉換 Google Sheet?</p>
                    <ol className="list-decimal pl-5 space-y-1">
                      <li>打開您的「醫宣歷年參與名單」Google Sheet</li>
                      <li>點選左上角 <strong>檔案 (File)</strong></li>
                      <li>選擇 <strong>下載 (Download)</strong> &rarr; <strong>逗號分隔值 (.csv)</strong></li>
                    </ol>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-sm text-gray-600">
                        目前系統內共有 <span className="font-bold text-indigo-600 text-xl">{historicalData.length}</span> 筆歷史資料
                      </div>
                    </div>

                    {historicalData.length > 0 && (
                      <button
                        onClick={handleClearHistoricalData}
                        className="w-full flex items-center justify-center px-4 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all text-sm font-bold btn-press"
                      >
                        <Trash2 size={16} className="mr-2" />
                        清空所有歷史資料
                      </button>
                    )}

                    {historicalData.length > 0 && (
                      <p className="text-xs text-gray-400 mt-2 text-center">
                        ⚠️ 清空前請確保已下載備份
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col justify-center items-center border-2 border-dashed border-gray-300 rounded-2xl p-8 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-400 transition-all group cursor-pointer relative btn-press min-h-[200px]">
                  <input type="file" accept=".csv" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileUpload} />
                  <Upload size={48} className="text-gray-400 group-hover:text-indigo-500 mb-4 transition-colors group-hover:scale-110" />
                  <p className="text-lg font-bold text-gray-700 group-hover:text-indigo-700 text-center">點擊上傳 CSV 檔案</p>
                  <p className="text-sm text-gray-400 mt-2 text-center">支援格式: 姓名, 教會, (年份或備註)</p>
                  <p className="text-xs text-gray-400 mt-3 text-center">上傳新檔案會新增資料，不會覆蓋現有資料</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showEditMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeEditMemberModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Pencil size={22} className="mr-2 text-indigo-600" /> 修改基本資料
              </h3>
              <button onClick={closeEditMemberModal} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              用來補齊卡片上的基本資料；職務與任務請在卡片下方的下拉選單調整。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">姓名 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editMemberForm.name}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">性別</label>
                <select
                  value={editMemberForm.gender}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, gender: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="">未填</option>
                  {Object.values(Gender).map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">教會</label>
                <input
                  type="text"
                  value={editMemberForm.church}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, church: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="例：彰興"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">電話</label>
                <input
                  type="text"
                  value={editMemberForm.phone}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="09xxxxxxxx"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editMemberForm.email}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="若要讓本人查詢/收信，請補 Email"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">醫宣角色</label>
                <input
                  type="text"
                  value={editMemberForm.role}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="例：西醫、護理、福音人員"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">其他角色說明</label>
                <input
                  type="text"
                  value={editMemberForm.otherRoleDescription}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, otherRoleDescription: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="角色為其他時可補充"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">學生專業</label>
                <input
                  type="text"
                  value={editMemberForm.studentSpecialization}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, studentSpecialization: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="例：醫學生、護理學生"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">西醫次專科</label>
                <input
                  type="text"
                  value={editMemberForm.westernMedicineSpecialty}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, westernMedicineSpecialty: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="例：家醫、骨科"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">治療師類別</label>
                <input
                  type="text"
                  value={editMemberForm.therapistSpecialty}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, therapistSpecialty: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="例：語言治療師、物理治療師"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">備註</label>
                <textarea
                  value={editMemberForm.message}
                  onChange={(e) => setEditMemberForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full min-h-24 px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 outline-none"
                  placeholder="可補充可報備日、特殊情況或行政備註"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={closeEditMemberModal} className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 font-bold text-gray-600 hover:bg-gray-50">取消</button>
              <button onClick={handleUpdateMemberProfile} disabled={isUpdatingMember} className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center">
                {isUpdatingMember ? '儲存中...' : <><Save size={18} className="mr-1.5" /> 儲存資料</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeAddMemberModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Users size={22} className="mr-2 text-green-600" /> 新增現場成員
              </h3>
              <button onClick={closeAddMemberModal} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              新增至梯隊：<span className="font-bold text-gray-800">{dashboardEvents.find(e => e.id === selectedEventId)?.dateRange || '（未選擇）'}</span>
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">姓名 <span className="text-red-500">*</span></label>
                <input type="text" value={addMemberForm.name} onChange={(e) => setAddMemberForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none" placeholder="必填" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">職務</label>
                  <select value={addMemberForm.leadershipRole} onChange={(e) => setAddMemberForm(f => ({ ...f, leadershipRole: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none">
                    {LEADERSHIP_ROLES.map(role => <option key={role} value={role === '無' ? '' : role}>{role}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">工作</label>
                  <select value={addMemberForm.taskRole} onChange={(e) => setAddMemberForm(f => ({ ...f, taskRole: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none">
                    {taskOptions.map(role => <option key={role} value={role === '尚未分配' ? '' : role}>{role}</option>)}
                  </select>
                </div>
              </div>
              {addMemberForm.taskRole === '其他' && (
                <div className="animate-fade-in">
                  <label className="block text-sm font-bold text-gray-700 mb-1">自訂工作</label>
                  <input
                    type="text"
                    value={addMemberForm.customTaskRole}
                    onChange={(e) => setAddMemberForm(f => ({ ...f, customTaskRole: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border-2 border-emerald-200 bg-emerald-50 text-emerald-900 placeholder-emerald-400 focus:border-emerald-500 outline-none"
                    placeholder="輸入自訂工作名稱"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">電話 <span className="text-gray-400 font-normal">（選填，填了本人才能自助查詢）</span></label>
                <input type="text" value={addMemberForm.phone} onChange={(e) => setAddMemberForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none" placeholder="09xxxxxxxx" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">性別 <span className="text-gray-400 font-normal">（選填）</span></label>
                <select value={addMemberForm.gender} onChange={(e) => setAddMemberForm(f => ({ ...f, gender: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none">
                  <option value="">未填</option>
                  {Object.values(Gender).map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">教會 <span className="text-gray-400 font-normal">（選填）</span></label>
                  <input type="text" value={addMemberForm.church} onChange={(e) => setAddMemberForm(f => ({ ...f, church: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">角色 <span className="text-gray-400 font-normal">（選填）</span></label>
                  <input type="text" value={addMemberForm.role} onChange={(e) => setAddMemberForm(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none" placeholder="如：當地同工" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">備註 <span className="text-gray-400 font-normal">（選填）</span></label>
                <input type="text" value={addMemberForm.note} onChange={(e) => setAddMemberForm(f => ({ ...f, note: e.target.value }))} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-green-500 outline-none" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={closeAddMemberModal} className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 font-bold text-gray-600 hover:bg-gray-50">取消</button>
              <button onClick={handleAddMember} disabled={isAddingMember} className="flex-1 py-2.5 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center">
                {isAddingMember ? '新增中...' : <><Save size={18} className="mr-1.5" /> 新增</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 🚀 使用 React.memo 優化性能 - 避免不必要的重新渲染
// 只有當 props 真正改變時才重新渲染（預計減少 30% 不必要渲染）
export default React.memo(Dashboard, (prevProps, nextProps) => {
  // 淺比較所有 props
  return (
    prevProps.systemYear === nextProps.systemYear &&
    prevProps.registrations === nextProps.registrations &&
    prevProps.historicalData === nextProps.historicalData &&
    prevProps.tripParticipants === nextProps.tripParticipants &&
    prevProps.watchlist === nextProps.watchlist &&
    prevProps.events === nextProps.events &&
    prevProps.onUpdateSystemYear === nextProps.onUpdateSystemYear &&
    prevProps.onUpdateHistoricalData === nextProps.onUpdateHistoricalData &&
    prevProps.onUpdateWatchlist === nextProps.onUpdateWatchlist &&
    prevProps.onUpdateEvents === nextProps.onUpdateEvents &&
    prevProps.onEditEvent === nextProps.onEditEvent &&
    prevProps.onUpdateEventAndRegistrants === nextProps.onUpdateEventAndRegistrants &&
    prevProps.onCreateRegistrant === nextProps.onCreateRegistrant &&
    prevProps.onUpdateRegistrant === nextProps.onUpdateRegistrant &&
    prevProps.currentUserLabel === nextProps.currentUserLabel &&
    prevProps.showToast === nextProps.showToast
  );
});
